/**
 * CollaborationCanvas - Main infinite canvas component for team collaboration
 * Phase 1 Enhanced + Phase 2 Features
 */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Plus, StickyNote, Square, Users, Share2, 
  ZoomIn, ZoomOut, Maximize2, Loader2, Clock,
  Trash2, Check, X, Circle, Eye, EyeOff,
  Hand, MousePointer2, Type, Minus, Shapes,
  RectangleHorizontal, CircleDot, Triangle, Info,
  ArrowUpToLine, ArrowDownToLine, Undo2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useSafeMode } from '../contexts/SafeModeContext';
import { useAuth } from '../contexts/AuthContext';
import { collaborationApi } from '../services/api';
import { useCollaborationSocket } from '../hooks/useCollaborationSocket';
import ShareModal from '../components/collaboration/ShareModal';
import CollaborationAIPanel from '../components/collaboration/CollaborationAIPanel';
import ProductElementsDrawer from '../components/collaboration/ProductElementsDrawer';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import { 
  FacilitationTimer, 
  PrivateModeToggle, 
  DotVotingPanel,
  VoteIndicator,
  FollowModeIndicator,
  ConnectorDefs 
} from '../components/collaboration/FacilitationTools';

// Expanded sticky note colors
const STICKY_COLORS = [
  { name: 'Yellow', value: '#fef08a' },
  { name: 'Pink', value: '#fecdd3' },
  { name: 'Blue', value: '#bfdbfe' },
  { name: 'Green', value: '#bbf7d0' },
  { name: 'Purple', value: '#ddd6fe' },
  { name: 'Orange', value: '#fed7aa' },
  { name: 'Teal', value: '#99f6e4' },
  { name: 'Rose', value: '#fda4af' },
  { name: 'Lime', value: '#bef264' },
  { name: 'Sky', value: '#7dd3fc' },
  { name: 'Amber', value: '#fcd34d' },
  { name: 'Indigo', value: '#c7d2fe' },
];

// Frame colors
const FRAME_COLORS = [
  { name: 'Gray', value: '#f3f4f6' },
  { name: 'Blue', value: '#dbeafe' },
  { name: 'Green', value: '#dcfce7' },
  { name: 'Yellow', value: '#fef9c3' },
  { name: 'Purple', value: '#f3e8ff' },
  { name: 'Pink', value: '#fce7f3' },
  { name: 'Cyan', value: '#cffafe' },
  { name: 'Orange', value: '#ffedd5' },
];

// Expanded cursor colors for 12 team members
const CURSOR_COLORS = [
  { name: 'Red', value: '#ef4444' },
  { name: 'Orange', value: '#f97316' },
  { name: 'Amber', value: '#f59e0b' },
  { name: 'Yellow', value: '#eab308' },
  { name: 'Lime', value: '#84cc16' },
  { name: 'Green', value: '#22c55e' },
  { name: 'Emerald', value: '#10b981' },
  { name: 'Cyan', value: '#06b6d4' },
  { name: 'Blue', value: '#3b82f6' },
  { name: 'Violet', value: '#8b5cf6' },
  { name: 'Purple', value: '#a855f7' },
  { name: 'Pink', value: '#ec4899' },
];

// Avatar sprite sheet for guests (9 columns x 4 rows = 36 animal avatars)
const AVATAR_SHEET_URL = 'https://customer-assets.emergentagent.com/job_b353f328-c785-48ef-8d8b-6ae791a336a7/artifacts/99xqh56e_set-of-funny-animal-portraits-vector.jpg';

// Get avatar style for sprite sheet positioning
const getAvatarStyle = (avatarId) => {
  if (!avatarId || avatarId < 1 || avatarId > 36) return null;
  const cols = 9;
  const row = Math.floor((avatarId - 1) / cols);
  const col = (avatarId - 1) % cols;
  return {
    backgroundImage: `url(${AVATAR_SHEET_URL})`,
    backgroundSize: '900% 400%',
    backgroundPosition: `${col * 12.5}% ${row * 33.33}%`,
  };
};

// Quick reaction emojis
const QUICK_EMOJIS = ['👍', '❤️', '🎉', '🔥', '💡', '⭐'];

// Zoom constraints
const MIN_ZOOM = 0.1;
const MAX_ZOOM = 3;
const ZOOM_SENSITIVITY = 0.001;

const CollaborationCanvas = () => {
  const { spaceId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { getThemeColors } = useSafeMode();
  const { user, loading: authLoading } = useAuth();
  const colors = getThemeColors();
  
  // Guest session state
  const [guestSession, setGuestSession] = useState(null);
  const [isGuest, setIsGuest] = useState(false);
  const isGuestRef = useRef(false); // Ref to track guest status for callbacks
  
  // Update ref when isGuest changes
  useEffect(() => {
    isGuestRef.current = isGuest;
  }, [isGuest]);
  
  // Current user (authenticated or guest)
  const currentUser = isGuest ? {
    user_id: guestSession?.guest_id,
    name: guestSession?.name,
    avatar_id: guestSession?.avatar_id,
    role: guestSession?.role,
    is_guest: true
  } : user;

  // Canvas state
  const [space, setSpace] = useState(null);
  const [elements, setElements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // View state
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });

  // Selection and editing state
  const [selectedElement, setSelectedElement] = useState(null);
  const [selectedElements, setSelectedElements] = useState([]); // Multi-select support
  const [editingElement, setEditingElement] = useState(null);
  const [draggedElement, setDraggedElement] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isDraggingMultiple, setIsDraggingMultiple] = useState(false); // For visual feedback
  const [dragStartPositions, setDragStartPositions] = useState([]); // Track original positions for undo
  
  // Lasso selection state
  const [isLassoing, setIsLassoing] = useState(false);
  const [lassoStart, setLassoStart] = useState({ x: 0, y: 0 });
  const [lassoEnd, setLassoEnd] = useState({ x: 0, y: 0 });
  const justCompletedLasso = useRef(false); // Prevent click from clearing lasso selection
  const justDragged = useRef(false); // Prevent click from firing after drag
  const mouseDownHandledSelection = useRef(false); // Prevent double-handling of selection
  
  // Resizing state
  const [resizingElement, setResizingElement] = useState(null);
  const [resizeHandle, setResizeHandle] = useState(null);
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const [resizeStartSize, setResizeStartSize] = useState(null); // Track original size for undo

  // Undo history
  const [undoStack, setUndoStack] = useState([]);
  const MAX_UNDO_HISTORY = 50;
  
  // Clipboard for copy/paste
  const [clipboard, setClipboard] = useState([]);

  // Track which right-side drawer is open (null = both collapsed, 'elements' or 'ai')
  const [openDrawer, setOpenDrawer] = useState(null);

  // Tool state - now supports more tools
  // 'grab' = pan/move, 'select' = select items, 'sticky' = create sticky, 'frame' = create frame
  // 'connector' = draw lines, 'text' = add text, 'pen' = freehand draw, 'shape' = add shapes
  const [activeTool, setActiveTool] = useState('grab'); // Default to grab tool
  const [currentColor, setCurrentColor] = useState(STICKY_COLORS[0].value);
  const [shapeType, setShapeType] = useState('rectangle'); // rectangle, circle, triangle, line

  // Cursor tracking
  const [remoteCursors, setRemoteCursors] = useState({});

  // Modals
  const [showShareModal, setShowShareModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  const [collaborators, setCollaborators] = useState([]);

  // Phase 2: Facilitation state
  const [isPrivateMode, setIsPrivateMode] = useState(false);
  const [isVotingActive, setIsVotingActive] = useState(false);
  const [votesPerPerson, setVotesPerPerson] = useState(3);
  const [isAnonymousVoting, setIsAnonymousVoting] = useState(false);
  const [userVotes, setUserVotes] = useState({}); // {elementId: voteCount}
  const [allVotes, setAllVotes] = useState({}); // {elementId: totalVotes}
  const [votingEnded, setVotingEnded] = useState(false); // Track if voting has ended (for showing final results)
  const [followingUser, setFollowingUser] = useState(null);
  const [connectorMode, setConnectorMode] = useState(false);
  const [connectorStart, setConnectorStart] = useState(null);
  const [connectors, setConnectors] = useState([]);
  const [selectedConnector, setSelectedConnector] = useState(null);
  const [showShortcutsModal, setShowShortcutsModal] = useState(false);
  
  // Timer synchronization state
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [syncedTimeLeft, setSyncedTimeLeft] = useState(0);
  const [timerStartedBy, setTimerStartedBy] = useState(null);

  const canvasRef = useRef(null);
  const lastCursorEmit = useRef(0);
  const guestSessionToken = useRef(null); // Store guest session token for API calls
  
  // Track delete functions for keyboard shortcut ref
  const deleteFunctionsRef = useRef({ deleteElement: null, deleteConnector: null });
  
  // Ref for anonymous voting to avoid stale closures in WebSocket handler
  const isAnonymousVotingRef = useRef(false);
  useEffect(() => {
    isAnonymousVotingRef.current = isAnonymousVoting;
  }, [isAnonymousVoting]);

  // API wrapper functions that handle both authenticated and guest users
  // Use refs to avoid stale closure issues with isGuest state
  const apiCreateElement = useCallback(async (elementData) => {
    if (isGuestRef.current && guestSessionToken.current) {
      return collaborationApi.guestCreateElement(spaceId, guestSessionToken.current, elementData);
    }
    return collaborationApi.createElement(spaceId, elementData);
  }, [spaceId]);

  const apiUpdateElement = useCallback(async (elementId, updateData) => {
    if (isGuestRef.current && guestSessionToken.current) {
      return collaborationApi.guestUpdateElement(spaceId, elementId, guestSessionToken.current, updateData);
    }
    return collaborationApi.updateElement(spaceId, elementId, updateData);
  }, [spaceId]);

  const apiDeleteElement = useCallback(async (elementId) => {
    if (isGuestRef.current && guestSessionToken.current) {
      // Use guest delete endpoint
      return collaborationApi.guestDeleteElement(spaceId, elementId, guestSessionToken.current);
    }
    return collaborationApi.deleteElement(spaceId, elementId);
  }, [spaceId]);

  // WebSocket event handler
  const handleSocketEvent = useCallback((eventType, data) => {
    switch (eventType) {
      case 'presence_update':
        break;
      case 'cursor_update':
        setRemoteCursors(prev => ({
          ...prev,
          [data.user_id]: {
            x: data.x,
            y: data.y,
            name: data.name,
            color: data.color,
          }
        }));
        break;
      case 'element_created':
        setElements(prev => [...prev, data]);
        break;
      case 'element_updated':
        setElements(prev => prev.map(el => 
          el.element_id === data.element_id ? data : el
        ));
        break;
      case 'element_deleted':
        setElements(prev => prev.filter(el => el.element_id !== data.element_id));
        break;
      case 'reaction_added':
        setElements(prev => prev.map(el => {
          if (el.element_id === data.element_id) {
            const reactions = [...(el.reactions || [])];
            if (!reactions.some(r => r.emoji === data.emoji && r.user_id === data.user_id)) {
              reactions.push({ emoji: data.emoji, user_id: data.user_id, user_name: data.user_name });
            }
            return { ...el, reactions };
          }
          return el;
        }));
        break;
      case 'reaction_removed':
        setElements(prev => prev.map(el => {
          if (el.element_id === data.element_id) {
            const reactions = (el.reactions || []).filter(
              r => !(r.emoji === data.emoji && r.user_id === data.user_id)
            );
            return { ...el, reactions };
          }
          return el;
        }));
        break;
      case 'voting_started':
        setIsVotingActive(true);
        setVotingEnded(false);
        setVotesPerPerson(data.votes_per_person || 3);
        setIsAnonymousVoting(data.anonymous || false);
        setUserVotes({});
        setAllVotes({});
        toast.info(`Voting started! Each person has ${data.votes_per_person || 3} votes.${data.anonymous ? ' (Anonymous)' : ''}`);
        break;
      case 'voting_ended':
        setIsVotingActive(false);
        setVotingEnded(true);
        // Use vote_totals directly if provided, otherwise calculate from final_votes
        if (data.vote_totals) {
          setAllVotes(data.vote_totals);
        } else if (data.final_votes) {
          const totals = {};
          for (const [elementId, userVotes] of Object.entries(data.final_votes)) {
            totals[elementId] = Object.values(userVotes).reduce((sum, v) => sum + v, 0);
          }
          setAllVotes(totals);
        }
        toast.info('Voting ended! Results are displayed on each item.');
        break;
      case 'voting_cleared':
        // Reset all votes but keep voting session if it was active
        setUserVotes({});
        setAllVotes({});
        setVotingEnded(false);
        toast.info('Votes have been cleared.');
        break;
      case 'vote_cast':
        // Only update allVotes in real-time if not anonymous voting
        // The backend now sends all_totals with each vote
        // Use ref to avoid stale closure
        if (!isAnonymousVotingRef.current && data.all_totals) {
          setAllVotes(data.all_totals);
        } else if (!isAnonymousVotingRef.current) {
          // Fallback: update single element total
          if (data.element_total !== undefined) {
            setAllVotes(prev => ({
              ...prev,
              [data.element_id]: data.element_total
            }));
          }
        }
        break;
      // Timer synchronization events
      case 'timer_started':
        setIsTimerRunning(true);
        setSyncedTimeLeft(data.time_seconds);
        setTimerStartedBy(data.started_by);
        toast.info(`Timer started by ${data.started_by}`);
        break;
      case 'timer_sync':
        setSyncedTimeLeft(data.time_left);
        setIsTimerRunning(data.is_running);
        break;
      case 'timer_paused':
        setIsTimerRunning(false);
        setSyncedTimeLeft(data.time_left);
        break;
      case 'timer_reset':
        setIsTimerRunning(false);
        setSyncedTimeLeft(0);
        setTimerStartedBy(null);
        break;
      case 'user_joined':
        toast.info(`${data.name} joined the space`);
        break;
      case 'user_left':
        setRemoteCursors(prev => {
          const newCursors = { ...prev };
          delete newCursors[data.user_id];
          return newCursors;
        });
        break;
      default:
        break;
    }
  }, []);

  // Check for guest session token in URL - must happen BEFORE WebSocket connection
  useEffect(() => {
    const guestToken = searchParams.get('guest');
    
    if (guestToken) {
      // Store the token for API calls
      guestSessionToken.current = guestToken;
      
      // Validate guest session
      const validateGuestSession = async () => {
        try {
          const response = await collaborationApi.validateGuestSession(guestToken);
          console.log('[Guest] Session validated:', response.data);
          setGuestSession(response.data);
          setIsGuest(true);
          
          // Store in localStorage for session persistence
          localStorage.setItem('guestSession', JSON.stringify({
            token: guestToken,
            spaceId: spaceId,
            data: response.data
          }));
        } catch (err) {
          console.error('[Guest] Invalid guest session:', err);
          // Check localStorage for stored guest session
          const storedSession = localStorage.getItem('guestSession');
          if (storedSession) {
            try {
              const parsed = JSON.parse(storedSession);
              if (parsed.spaceId === spaceId) {
                guestSessionToken.current = parsed.token;
                const validateResponse = await collaborationApi.validateGuestSession(parsed.token);
                console.log('[Guest] Restored session from localStorage:', validateResponse.data);
                setGuestSession(validateResponse.data);
                setIsGuest(true);
              }
            } catch (e) {
              localStorage.removeItem('guestSession');
              guestSessionToken.current = null;
              setError('Your session has expired. Please rejoin via the share link.');
            }
          } else {
            setError('Invalid or expired guest session. Please rejoin via the share link.');
          }
        }
      };
      validateGuestSession();
    }
  }, [searchParams, spaceId]);

  // Compute effective user for WebSocket - either authenticated user or validated guest
  const effectiveUser = useMemo(() => {
    if (user) {
      return user;
    }
    if (isGuest && guestSession) {
      return {
        user_id: guestSession.guest_id,
        name: guestSession.name || 'Guest',
        avatar_id: guestSession.avatar_id,
        role: guestSession.role,
        is_guest: true
      };
    }
    return null;
  }, [user, isGuest, guestSession]);

  // WebSocket connection - only connect when we have a valid user (authenticated or guest)
  const {
    connected,
    activeUsers,
    emitCursorMove,
    emitElementCreate,
    emitElementUpdate,
    emitElementDelete,
    emitReactionAdd,
    emitReactionRemove,
    emitVotingStart,
    emitVotingEnd,
    emitVotingClear,
    emitVoteCast,
    emitTimerStart,
    emitTimerSync,
    emitTimerPause,
    emitTimerReset,
  } = useCollaborationSocket(spaceId, effectiveUser, handleSocketEvent);

  // Load space data
  useEffect(() => {
    const loadSpace = async () => {
      // Wait for guest validation if guest token present
      const guestToken = searchParams.get('guest');
      if (guestToken && !isGuest && !guestSession) {
        return; // Wait for guest session to be validated
      }
      
      try {
        setLoading(true);
        
        // For guests, use the guest-specific endpoint
        if (isGuest && guestSession) {
          const guestSessionToken = searchParams.get('guest') || 
            (localStorage.getItem('guestSession') ? JSON.parse(localStorage.getItem('guestSession')).token : null);
          
          if (guestSessionToken) {
            const spaceResponse = await collaborationApi.guestGetSpace(guestSessionToken);
            setSpace({
              space_id: spaceResponse.data.space_id,
              name: spaceResponse.data.name,
              owner_name: spaceResponse.data.owner_name,
              is_owner: false,
              is_guest: true,
              permission: spaceResponse.data.permission || 'view',
              canvas_state: spaceResponse.data.canvas_state
            });
            setElements(spaceResponse.data.elements || []);
            
            if (spaceResponse.data.canvas_state) {
              setZoom(spaceResponse.data.canvas_state.zoom || 1);
              setPan({
                x: spaceResponse.data.canvas_state.panX || 0,
                y: spaceResponse.data.canvas_state.panY || 0,
              });
            }
          }
        } else {
          const response = await collaborationApi.getSpace(spaceId);
          setSpace(response.data);
          setElements(response.data.elements || []);
          
          if (response.data.canvas_state) {
            setZoom(response.data.canvas_state.zoom || 1);
            setPan({
              x: response.data.canvas_state.panX || 0,
              y: response.data.canvas_state.panY || 0,
            });
          }
        }
      } catch (err) {
        console.error('Failed to load space:', err);
        setError(err.response?.data?.detail || 'Failed to load collaboration space');
      } finally {
        setLoading(false);
      }
    };

    if (spaceId && (user || isGuest)) {
      loadSpace();
    }
  }, [spaceId, user, isGuest, guestSession, searchParams]);

  // Load collaborators when share modal opens
  useEffect(() => {
    const loadCollaborators = async () => {
      if (showShareModal && spaceId) {
        try {
          const response = await collaborationApi.listCollaborators(spaceId);
          setCollaborators(response.data);
        } catch (err) {
          console.error('Failed to load collaborators:', err);
        }
      }
    };
    loadCollaborators();
  }, [showShareModal, spaceId]);

  // Handle ESC key to cancel tool and keyboard shortcuts
  // NOTE: Zoom and delete functions are accessed via refs to avoid dependency issues
  const zoomFunctionsRef = useRef({ zoomIn: null, zoomOut: null, zoomReset: null });
  const clipboardFunctionsRef = useRef({ copy: null, cut: null, paste: null, undo: null, deleteSelected: null });
  
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't handle shortcuts when typing in input/textarea
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      
      if (e.key === 'Escape') {
        setActiveTool(null);
        setSelectedElement(null);
        setSelectedElements([]);
        setEditingElement(null);
        setConnectorMode(false);
        setConnectorStart(null);
        setFollowingUser(null);
      }
      
      // Meta/Ctrl shortcuts (Copy, Paste, Cut, Undo)
      if (e.metaKey || e.ctrlKey) {
        switch (e.key.toLowerCase()) {
          case 'c': // Copy
            e.preventDefault();
            clipboardFunctionsRef.current.copy?.();
            break;
          case 'x': // Cut
            e.preventDefault();
            clipboardFunctionsRef.current.cut?.();
            break;
          case 'v': // Paste
            e.preventDefault();
            clipboardFunctionsRef.current.paste?.();
            break;
          case 'z': // Undo
            e.preventDefault();
            clipboardFunctionsRef.current.undo?.();
            break;
          default:
            break;
        }
        return;
      }
      
      // Keyboard shortcuts (Miro/Figma-like)
      if (!e.metaKey && !e.ctrlKey && !e.altKey) {
        switch (e.key.toLowerCase()) {
          case 'v': // Select tool
            setActiveTool('select');
            setConnectorMode(false);
            break;
          case 'h': // Hand/grab tool
            setActiveTool('grab');
            setConnectorMode(false);
            break;
          case 's': // Sticky note
            e.preventDefault();
            setActiveTool('sticky');
            setConnectorMode(false);
            break;
          case 'f': // Frame
            setActiveTool('frame');
            setConnectorMode(false);
            break;
          case 'l': // Line shape
            setActiveTool('shape');
            setShapeType('line');
            setConnectorMode(false);
            break;
          case 't': // Text
            setActiveTool('text');
            setConnectorMode(false);
            break;
          case 'r': // Rectangle shape
            setActiveTool('shape');
            setShapeType('rectangle');
            setConnectorMode(false);
            break;
          case 'o': // Oval/circle shape
            setActiveTool('shape');
            setShapeType('circle');
            setConnectorMode(false);
            break;
          case '+':
          case '=':
            zoomFunctionsRef.current.zoomIn?.();
            break;
          case '-':
            zoomFunctionsRef.current.zoomOut?.();
            break;
          case '0':
            zoomFunctionsRef.current.zoomReset?.();
            break;
          default:
            break;
        }
        
        // Handle Delete/Backspace separately (they don't lowercase well)
        if (e.key === 'Delete' || e.key === 'Backspace') {
          // Handle multi-select delete
          if ((selectedElements.length > 0 || selectedElement) && !editingElement) {
            e.preventDefault();
            clipboardFunctionsRef.current.deleteSelected?.();
            setActiveTool('grab');
          }
          if (selectedConnector) {
            e.preventDefault();
            deleteFunctionsRef.current.deleteConnector?.(selectedConnector);
          }
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedElement, selectedElements, editingElement, selectedConnector]);

  // Handle mouse wheel zoom
  const handleWheel = useCallback((e) => {
    if (!canvasRef.current) return;
    e.preventDefault();
    
    const rect = canvasRef.current.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    
    // Calculate zoom delta
    const delta = -e.deltaY * ZOOM_SENSITIVITY;
    const newZoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom * (1 + delta)));
    
    // Zoom towards mouse position
    const zoomRatio = newZoom / zoom;
    const newPanX = mouseX - (mouseX - pan.x) * zoomRatio;
    const newPanY = mouseY - (mouseY - pan.y) * zoomRatio;
    
    setZoom(newZoom);
    setPan({ x: newPanX, y: newPanY });
  }, [zoom, pan]);

  // Add wheel event listener
  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      canvas.addEventListener('wheel', handleWheel, { passive: false });
      return () => canvas.removeEventListener('wheel', handleWheel);
    }
  }, [handleWheel]);

  // Handle mouse move for cursor tracking, panning, dragging, resizing, and lasso
  const handleCanvasMouseMove = useCallback((e) => {
    if (!canvasRef.current) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left - pan.x) / zoom;
    const y = (e.clientY - rect.top - pan.y) / zoom;

    // Throttle cursor updates to 60fps
    const now = Date.now();
    if (now - lastCursorEmit.current > 16) {
      emitCursorMove(x, y);
      lastCursorEmit.current = now;
    }

    // Handle lasso selection
    if (isLassoing) {
      setLassoEnd({ x, y });
      return;
    }

    // Handle panning
    if (isPanning) {
      const dx = e.clientX - panStart.x;
      const dy = e.clientY - panStart.y;
      setPan({ x: pan.x + dx, y: pan.y + dy });
      setPanStart({ x: e.clientX, y: e.clientY });
      return;
    }

    // Handle element resizing
    if (resizingElement && resizeHandle) {
      const element = elements.find(el => el.element_id === resizingElement);
      if (element) {
        let newWidth = resizeStart.width;
        let newHeight = resizeStart.height;
        let newX = resizeStart.posX;
        let newY = resizeStart.posY;
        
        const dx = x - resizeStart.x;
        const dy = y - resizeStart.y;
        
        // East handle: increase width
        if (resizeHandle.includes('e')) {
          newWidth = Math.max(100, resizeStart.width + dx);
        }
        // West handle: increase width and move position left
        if (resizeHandle.includes('w')) {
          const widthChange = Math.min(dx, resizeStart.width - 100); // Limit so width doesn't go below 100
          newWidth = Math.max(100, resizeStart.width - dx);
          newX = resizeStart.posX + dx;
          // Clamp position so element doesn't flip
          if (newWidth === 100) {
            newX = resizeStart.posX + resizeStart.width - 100;
          }
        }
        // South handle: increase height
        if (resizeHandle.includes('s')) {
          newHeight = Math.max(80, resizeStart.height + dy);
        }
        // North handle: increase height and move position up
        if (resizeHandle.includes('n')) {
          newHeight = Math.max(80, resizeStart.height - dy);
          newY = resizeStart.posY + dy;
          // Clamp position so element doesn't flip
          if (newHeight === 80) {
            newY = resizeStart.posY + resizeStart.height - 80;
          }
        }
        
        setElements(prev => prev.map(el =>
          el.element_id === resizingElement
            ? { 
                ...el, 
                size: { width: newWidth, height: newHeight },
                position: { x: newX, y: newY }
              }
            : el
        ));
      }
      return;
    }

    // Handle element dragging (including multi-select drag)
    if (draggedElement) {
      const newX = x - dragOffset.x;
      const newY = y - dragOffset.y;
      const dx = newX - draggedElement.position.x;
      const dy = newY - draggedElement.position.y;
      
      // If multiple elements are selected AND we're dragging one of them, move them all
      if (selectedElements.length > 1 && selectedElements.includes(draggedElement.element_id)) {
        setElements(prev => prev.map(el =>
          selectedElements.includes(el.element_id)
            ? { ...el, position: { x: el.position.x + dx, y: el.position.y + dy } }
            : el
        ));
        // Update the drag reference position
        setDraggedElement(prev => ({
          ...prev,
          position: { x: newX, y: newY }
        }));
      } else {
        // Single element drag
        setElements(prev => prev.map(el =>
          el.element_id === draggedElement.element_id
            ? { ...el, position: { x: newX, y: newY } }
            : el
        ));
      }
    }
  }, [pan, zoom, isPanning, panStart, draggedElement, dragOffset, resizingElement, resizeHandle, resizeStart, elements, emitCursorMove, isLassoing, selectedElements]);
  
  // Check if element is within lasso bounds
  const isElementInLassoBounds = (element, startPos, endPos) => {
    const minX = Math.min(startPos.x, endPos.x);
    const maxX = Math.max(startPos.x, endPos.x);
    const minY = Math.min(startPos.y, endPos.y);
    const maxY = Math.max(startPos.y, endPos.y);
    
    const elX = element.position.x;
    const elY = element.position.y;
    const elWidth = element.size?.width || (element.type === 'sticky' ? 200 : 150);
    const elHeight = element.size?.height || (element.type === 'sticky' ? 150 : 150);
    
    // Check if element center or any part overlaps with lasso rect
    const elCenterX = elX + elWidth / 2;
    const elCenterY = elY + elHeight / 2;
    
    // Check if element overlaps with lasso rect (any overlap counts)
    return !(elX + elWidth < minX || elX > maxX || elY + elHeight < minY || elY > maxY);
  };

  // Complete lasso selection - called directly with current positions
  const completeLassoSelection = () => {
    if (!isLassoing) return;
    
    // Calculate selection based on current lasso positions
    const selected = elements.filter(el => isElementInLassoBounds(el, lassoStart, lassoEnd)).map(el => el.element_id);
    
    console.log('[Lasso] Completing selection:', { lassoStart, lassoEnd, selectedCount: selected.length });
    
    if (selected.length > 0) {
      setSelectedElements(selected);
      setSelectedElement(selected[0]);
      // Prevent the subsequent click from clearing selection
      justCompletedLasso.current = true;
      setTimeout(() => { justCompletedLasso.current = false; }, 100);
    }
    
    setIsLassoing(false);
    setLassoStart({ x: 0, y: 0 });
    setLassoEnd({ x: 0, y: 0 });
  };

  // Handle canvas click for creating elements
  const handleCanvasClick = async (e) => {
    // Only process clicks on canvas itself (not on elements)
    if (e.target !== canvasRef.current) return;
    
    const rect = canvasRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left - pan.x) / zoom;
    const y = (e.clientY - rect.top - pan.y) / zoom;

    // Check if guest has permission to create/edit
    const canEdit = !isGuest || guestSession?.role === 'edit';

    // Handle element creation tools
    if (activeTool === 'sticky' || activeTool === 'frame') {
      if (!canEdit) {
        toast.error("You don't have permission to create elements");
        setActiveTool('select');
        return;
      }
      
      const elementData = {
        type: activeTool,
        position: { x, y },
        content: activeTool === 'sticky' ? '' : '',
        color: activeTool === 'frame' ? FRAME_COLORS[0].value : currentColor,
        frame_title: activeTool === 'frame' ? 'New Frame' : '',
      };

      try {
        const response = await apiCreateElement(elementData);
        const newElement = response.data;
        setElements(prev => [...prev, newElement]);
        emitElementCreate(newElement);
        
        if (activeTool === 'sticky') {
          setEditingElement(newElement.element_id);
        }
        
        // Switch back to select tool after creating element
        setActiveTool('select');
      } catch (err) {
        toast.error('Failed to create element');
      }
    } else if (activeTool === 'text') {
      if (!canEdit) {
        toast.error("You don't have permission to create elements");
        setActiveTool('select');
        return;
      }
      
      // Text tool creates a transparent text element
      const elementData = {
        type: 'text',
        position: { x, y },
        content: '',
        color: 'transparent', // Transparent background
        size: { width: 200, height: 40 },
      };

      try {
        const response = await apiCreateElement(elementData);
        const newElement = response.data;
        setElements(prev => [...prev, newElement]);
        emitElementCreate(newElement);
        setEditingElement(newElement.element_id);
        setActiveTool('select');
      } catch (err) {
        toast.error('Failed to create text');
      }
    } else if (activeTool === 'shape') {
      if (!canEdit) {
        toast.error("You don't have permission to create elements");
        setActiveTool('select');
        return;
      }
      
      // Shape tool - creates actual shapes (circle, triangle, rectangle, line)
      const shapeColors = {
        rectangle: '#dbeafe', // Blue
        circle: '#dcfce7', // Green
        triangle: '#fef9c3', // Yellow
        line: '#374151', // Gray for lines
      };
      
      // Different default sizes for different shapes
      const shapeSizes = {
        rectangle: { width: 150, height: 150 },
        circle: { width: 150, height: 150 },
        triangle: { width: 150, height: 150 },
        line: { width: 200, height: 10 }, // Lines are wide and thin
      };
      
      const elementData = {
        type: 'shape',
        position: { x, y },
        content: '',
        color: shapeColors[shapeType] || '#dbeafe',
        shape_type: shapeType, // 'rectangle', 'circle', 'triangle', 'line'
        size: shapeSizes[shapeType] || { width: 150, height: 150 },
      };

      try {
        const response = await apiCreateElement(elementData);
        const newElement = response.data;
        setElements(prev => [...prev, newElement]);
        emitElementCreate(newElement);
        setActiveTool('select');
      } catch (err) {
        toast.error('Failed to create shape');
      }
    } else if (activeTool === 'select' || activeTool === 'grab') {
      // Deselect when clicking on empty canvas with select/grab tool
      // But NOT if we just completed a lasso selection
      if (!justCompletedLasso.current) {
        setSelectedElement(null);
        setSelectedElements([]);
        setSelectedConnector(null);
      }
    }
  };

  // Handle element drag start
  const handleElementDragStart = (e, element) => {
    if (resizingElement) return;
    // Only allow dragging in grab or select mode
    if (activeTool !== 'grab' && activeTool !== 'select') return;
    
    e.stopPropagation();
    
    const rect = canvasRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left - pan.x) / zoom;
    const y = (e.clientY - rect.top - pan.y) / zoom;
    
    // Support both Shift and Ctrl/Cmd for multi-select (cross-platform)
    const isMultiSelectKey = e.shiftKey || e.ctrlKey || e.metaKey;
    
    // Mark that mouseDown handled the selection (to prevent click from toggling)
    if (isMultiSelectKey) {
      mouseDownHandledSelection.current = true;
      setTimeout(() => { mouseDownHandledSelection.current = false; }, 300);
    }
    
    // Determine final selection state
    let finalSelection;
    
    if (isMultiSelectKey) {
      if (selectedElements.includes(element.element_id)) {
        // Toggle: remove from selection if already selected
        finalSelection = selectedElements.filter(id => id !== element.element_id);
      } else {
        // Add to selection
        finalSelection = [...selectedElements, element.element_id];
      }
    } else if (selectedElements.includes(element.element_id)) {
      // Already part of multi-selection - keep current selection for group drag
      finalSelection = [...selectedElements];
    } else {
      // Single select - replace selection
      finalSelection = [element.element_id];
    }
    
    setSelectedElements(finalSelection);
    
    // Update primary selection
    if (finalSelection.length > 0) {
      setSelectedElement(finalSelection.includes(element.element_id) ? element.element_id : finalSelection[0]);
    } else {
      setSelectedElement(null);
    }
    
    setIsDraggingMultiple(finalSelection.length > 1);
    
    // Only start drag if element is still selected
    if (finalSelection.includes(element.element_id)) {
      setDraggedElement(element);
      setDragOffset({
        x: x - element.position.x,
        y: y - element.position.y,
      });
      
      // Save original positions for undo
      const positionsToSave = finalSelection.length > 1
        ? elements.filter(el => finalSelection.includes(el.element_id)).map(el => ({
            element_id: el.element_id,
            oldPosition: { ...el.position }
          }))
        : [{ element_id: element.element_id, oldPosition: { ...element.position } }];
      setDragStartPositions(positionsToSave);
    } else {
      setDraggedElement(null);
      setDragStartPositions([]);
    }
  };
  
  // Handle element click (for selection without drag)
  const handleElementClick = (e, element) => {
    e.stopPropagation();
    
    // Don't process click if we just finished dragging
    if (justDragged.current) {
      return;
    }
    
    // Don't process click if mouseDown already handled multi-select
    if (mouseDownHandledSelection.current) {
      return;
    }
    
    // Support both Shift and Ctrl/Cmd for multi-select (cross-platform)
    const isMultiSelectKey = e.shiftKey || e.ctrlKey || e.metaKey;
    
    if (isMultiSelectKey) {
      // Toggle selection with shift/ctrl
      setSelectedElements(prev => {
        if (prev.includes(element.element_id)) {
          // Remove from selection
          const newSelection = prev.filter(id => id !== element.element_id);
          // Update primary selection if removed
          if (selectedElement === element.element_id && newSelection.length > 0) {
            setSelectedElement(newSelection[0]);
          } else if (newSelection.length === 0) {
            setSelectedElement(null);
          }
          return newSelection;
        }
        // Add to selection
        return [...prev, element.element_id];
      });
      // Set as primary selection if adding
      if (!selectedElements.includes(element.element_id)) {
        setSelectedElement(element.element_id);
      }
    } else {
      // Single select - clear previous selection
      setSelectedElement(element.element_id);
      setSelectedElements([element.element_id]);
    }
  };

  // Handle resize start
  const handleResizeStart = (e, element, handle) => {
    e.stopPropagation();
    e.preventDefault();
    
    const rect = canvasRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left - pan.x) / zoom;
    const y = (e.clientY - rect.top - pan.y) / zoom;
    
    setResizingElement(element.element_id);
    setResizeHandle(handle);
    setResizeStart({
      x,
      y,
      width: element.size?.width || 400,
      height: element.size?.height || 300,
      posX: element.position?.x || 0,
      posY: element.position?.y || 0,
    });
    // Save original size and position for undo
    setResizeStartSize({
      element_id: element.element_id,
      oldSize: { ...element.size },
      oldPosition: { ...element.position }
    });
  };

  // Handle drag/resize end
  const handleDragEnd = async () => {
    if (draggedElement) {
      // Mark that we just dragged to prevent click handler from toggling selection
      justDragged.current = true;
      setTimeout(() => { justDragged.current = false; }, 100);
      
      // Check if position actually changed (to avoid unnecessary undo entries)
      const hasMoved = dragStartPositions.some(startPos => {
        const currentEl = elements.find(el => el.element_id === startPos.element_id);
        return currentEl && (
          currentEl.position.x !== startPos.oldPosition.x || 
          currentEl.position.y !== startPos.oldPosition.y
        );
      });
      
      // Save to undo stack if moved
      if (hasMoved && dragStartPositions.length > 0) {
        pushToUndoStack({ 
          type: 'move', 
          elements: dragStartPositions.map(pos => ({
            element_id: pos.element_id,
            oldPosition: pos.oldPosition,
            newPosition: { ...elements.find(el => el.element_id === pos.element_id)?.position }
          }))
        });
      }
      
      // If dragging multiple elements, save all of them
      if (isDraggingMultiple && selectedElements.length > 1) {
        const updatePromises = selectedElements.map(async (elId) => {
          const element = elements.find(el => el.element_id === elId);
          if (element) {
            try {
              await apiUpdateElement(element.element_id, {
                position: element.position,
              });
              emitElementUpdate(element);
            } catch (err) {
              console.error('Failed to update element:', elId, err);
            }
          }
        });
        await Promise.all(updatePromises);
      } else {
        const element = elements.find(el => el.element_id === draggedElement.element_id);
        if (element) {
          try {
            await apiUpdateElement(element.element_id, {
              position: element.position,
            });
            emitElementUpdate(element);
          } catch (err) {
            toast.error('Failed to update position');
          }
        }
      }
      setDraggedElement(null);
      setIsDraggingMultiple(false);
      setDragStartPositions([]);
    }
    
    if (resizingElement) {
      const element = elements.find(el => el.element_id === resizingElement);
      if (element) {
        // Check if size or position actually changed
        const hasResized = resizeStartSize && (
          element.size?.width !== resizeStartSize.oldSize?.width ||
          element.size?.height !== resizeStartSize.oldSize?.height ||
          element.position?.x !== resizeStartSize.oldPosition?.x ||
          element.position?.y !== resizeStartSize.oldPosition?.y
        );
        
        // Save to undo stack if resized
        if (hasResized && resizeStartSize) {
          pushToUndoStack({
            type: 'resize',
            element_id: resizeStartSize.element_id,
            oldSize: resizeStartSize.oldSize,
            oldPosition: resizeStartSize.oldPosition,
            newSize: { ...element.size },
            newPosition: { ...element.position }
          });
        }
        
        try {
          await apiUpdateElement(element.element_id, {
            size: element.size,
            position: element.position, // Also save position for west-side resize
          });
          emitElementUpdate(element);
        } catch (err) {
          toast.error('Failed to update size');
        }
      }
      setResizingElement(null);
      setResizeHandle(null);
      setResizeStartSize(null);
    }
    
    // Complete lasso selection (only if we were lassoing, not dragging an element)
    if (isLassoing && !draggedElement) {
      completeLassoSelection();
    } else {
      setIsLassoing(false);
    }
    
    setIsPanning(false);
  };

  // Handle element content update
  const handleElementContentUpdate = async (elementId, content, isFrameTitle = false) => {
    try {
      const updateData = isFrameTitle ? { frame_title: content } : { content };
      const response = await apiUpdateElement(elementId, updateData);
      setElements(prev => prev.map(el =>
        el.element_id === elementId ? response.data : el
      ));
      emitElementUpdate(response.data);
    } catch (err) {
      toast.error('Failed to update content');
    }
  };

  // Handle sticky title update (for product element stickies)
  const handleStickyTitleUpdate = async (elementId, stickyTitle) => {
    try {
      const response = await apiUpdateElement(elementId, { sticky_title: stickyTitle });
      setElements(prev => prev.map(el =>
        el.element_id === elementId ? response.data : el
      ));
      emitElementUpdate(response.data);
    } catch (err) {
      toast.error('Failed to update title');
    }
  };

  // Handle element color update
  const handleElementColorUpdate = async (elementId, color) => {
    try {
      const response = await apiUpdateElement(elementId, { color });
      setElements(prev => prev.map(el =>
        el.element_id === elementId ? response.data : el
      ));
      emitElementUpdate(response.data);
    } catch (err) {
      toast.error('Failed to update color');
    }
  };

  // Handle element delete
  const handleElementDelete = async (elementId) => {
    try {
      // Save to undo stack before deleting
      const elementToDelete = elements.find(el => el.element_id === elementId);
      if (elementToDelete) {
        pushToUndoStack({ type: 'delete', elements: [elementToDelete] });
      }
      
      const result = await apiDeleteElement(elementId);
      if (result) {
        setElements(prev => prev.filter(el => el.element_id !== elementId));
        emitElementDelete(elementId);
        setSelectedElement(null);
      }
    } catch (err) {
      toast.error('Failed to delete element');
    }
  };

  // Handle multi-select delete
  const handleDeleteSelected = async () => {
    if (selectedElements.length === 0 && !selectedElement) return;
    
    // selectedElements contains IDs, we need to get actual element objects
    const elementsToDelete = selectedElements.length > 0 
      ? elements.filter(el => selectedElements.includes(el.element_id))
      : (selectedElement ? [elements.find(el => el.element_id === selectedElement)] : []);
    
    if (elementsToDelete.length === 0) return;
    
    // Save to undo stack
    pushToUndoStack({ type: 'delete', elements: elementsToDelete.filter(Boolean).map(el => ({ ...el })) });
    
    try {
      for (const element of elementsToDelete) {
        if (element) {
          await apiDeleteElement(element.element_id);
          emitElementDelete(element.element_id);
        }
      }
      setElements(prev => prev.filter(el => 
        !elementsToDelete.some(del => del?.element_id === el.element_id)
      ));
      setSelectedElement(null);
      setSelectedElements([]);
      toast.success(`Deleted ${elementsToDelete.length} item(s)`);
    } catch (err) {
      console.error('Delete error:', err);
      toast.error('Failed to delete elements');
    }
  };

  // Undo functionality
  const pushToUndoStack = (action) => {
    setUndoStack(prev => {
      const newStack = [...prev, action];
      if (newStack.length > MAX_UNDO_HISTORY) {
        return newStack.slice(-MAX_UNDO_HISTORY);
      }
      return newStack;
    });
  };

  const handleUndo = async () => {
    if (undoStack.length === 0) {
      toast.info('Nothing to undo');
      return;
    }
    
    const lastAction = undoStack[undoStack.length - 1];
    setUndoStack(prev => prev.slice(0, -1));
    
    try {
      switch (lastAction.type) {
        case 'delete': {
          // Re-create deleted elements
          for (const element of lastAction.elements) {
            const response = await apiCreateElement({
              type: element.type,
              position: element.position,
              content: element.content,
              color: element.color,
              size: element.size,
              frame_title: element.frame_title,
              z_index: element.z_index,
            });
            const newEl = response?.data || response;
            if (newEl) {
              setElements(prev => [...prev, newEl]);
              emitElementCreate(newEl);
            }
          }
          toast.success(`Restored ${lastAction.elements.length} item(s)`);
          break;
        }
        case 'create': {
          // Delete created elements
          for (const elementId of lastAction.elementIds) {
            await apiDeleteElement(elementId);
            emitElementDelete(elementId);
          }
          setElements(prev => prev.filter(el => !lastAction.elementIds.includes(el.element_id)));
          toast.success('Undid creation');
          break;
        }
        case 'move': {
          // Restore previous positions
          for (const item of lastAction.elements) {
            await apiUpdateElement(item.element_id, { position: item.oldPosition });
            setElements(prev => prev.map(el => 
              el.element_id === item.element_id ? { ...el, position: item.oldPosition } : el
            ));
            // Emit update for real-time sync
            const updatedEl = elements.find(el => el.element_id === item.element_id);
            if (updatedEl) {
              emitElementUpdate({ ...updatedEl, position: item.oldPosition });
            }
          }
          toast.success('Undid move');
          break;
        }
        case 'resize': {
          // Restore previous size and position
          await apiUpdateElement(lastAction.element_id, { 
            size: lastAction.oldSize,
            position: lastAction.oldPosition 
          });
          setElements(prev => prev.map(el => 
            el.element_id === lastAction.element_id 
              ? { ...el, size: lastAction.oldSize, position: lastAction.oldPosition } 
              : el
          ));
          const updatedEl = elements.find(el => el.element_id === lastAction.element_id);
          if (updatedEl) {
            emitElementUpdate({ ...updatedEl, size: lastAction.oldSize, position: lastAction.oldPosition });
          }
          toast.success('Undid resize');
          break;
        }
        default:
          break;
      }
    } catch (err) {
      console.error('Undo failed:', err);
      toast.error('Failed to undo');
    }
  };

  // Copy/Paste functionality
  const handleCopy = () => {
    // selectedElements contains IDs, we need to get actual element objects
    const elementsToCopy = selectedElements.length > 0 
      ? elements.filter(el => selectedElements.includes(el.element_id))
      : (selectedElement ? [elements.find(el => el.element_id === selectedElement)] : []);
    
    if (elementsToCopy.length === 0 || !elementsToCopy[0]) {
      toast.info('Nothing selected to copy');
      return;
    }
    
    // Deep copy elements to clipboard
    setClipboard(elementsToCopy.filter(Boolean).map(el => ({ ...el })));
    toast.success(`Copied ${elementsToCopy.length} item(s)`);
  };

  const handleCut = async () => {
    handleCopy();
    await handleDeleteSelected();
  };

  const handlePaste = async () => {
    if (clipboard.length === 0) {
      toast.info('Nothing to paste');
      return;
    }
    
    const PASTE_OFFSET = 30; // Offset for pasted elements
    const createdIds = [];
    
    try {
      for (const element of clipboard) {
        const response = await apiCreateElement({
          type: element.type,
          position: {
            x: element.position.x + PASTE_OFFSET,
            y: element.position.y + PASTE_OFFSET,
          },
          content: element.content,
          color: element.color,
          size: element.size,
          frame_title: element.frame_title,
        });
        const newEl = response?.data || response;
        if (newEl) {
          setElements(prev => [...prev, newEl]);
          emitElementCreate(newEl);
          createdIds.push(newEl.element_id);
        }
      }
      
      // Save to undo stack
      pushToUndoStack({ type: 'create', elementIds: createdIds });
      
      toast.success(`Pasted ${clipboard.length} item(s)`);
    } catch (err) {
      toast.error('Failed to paste');
    }
  };

  // Handle bring forward / send backward (z-index control)
  // Frames are always at z-index 0 (bottom), other elements have z-index >= 1
  const handleBringForward = async (elementId) => {
    const element = elements.find(el => el.element_id === elementId);
    if (!element || element.type === 'frame') return; // Frames stay at bottom
    
    // Find the maximum z-index among non-frame elements
    const maxZIndex = Math.max(...elements
      .filter(el => el.type !== 'frame')
      .map(el => el.z_index || 1), 0);
    
    const newZIndex = maxZIndex + 1;
    
    try {
      const response = await apiUpdateElement(elementId, { z_index: newZIndex });
      setElements(prev => prev.map(el =>
        el.element_id === elementId ? { ...el, z_index: newZIndex } : el
      ));
      emitElementUpdate({ ...element, z_index: newZIndex });
    } catch (err) {
      // Update locally even if API fails (for guests)
      setElements(prev => prev.map(el =>
        el.element_id === elementId ? { ...el, z_index: newZIndex } : el
      ));
    }
  };

  const handleSendBackward = async (elementId) => {
    const element = elements.find(el => el.element_id === elementId);
    if (!element || element.type === 'frame') return; // Frames stay at bottom
    
    // Find the minimum z-index among non-frame elements (but keep above 0 to stay above frames)
    const minZIndex = Math.min(...elements
      .filter(el => el.type !== 'frame' && el.element_id !== elementId)
      .map(el => el.z_index || 1), 1);
    
    const newZIndex = Math.max(minZIndex - 1, 1); // Keep at least 1 to stay above frames
    
    try {
      const response = await apiUpdateElement(elementId, { z_index: newZIndex });
      setElements(prev => prev.map(el =>
        el.element_id === elementId ? { ...el, z_index: newZIndex } : el
      ));
      emitElementUpdate({ ...element, z_index: newZIndex });
    } catch (err) {
      // Update locally even if API fails (for guests)
      setElements(prev => prev.map(el =>
        el.element_id === elementId ? { ...el, z_index: newZIndex } : el
      ));
    }
  };

  // AI Panel handlers
  const handleCenterElement = (elementId) => {
    const element = elements.find(el => el.element_id === elementId);
    if (!element) return;
    
    // Calculate center of canvas
    const canvasRect = canvasRef.current?.getBoundingClientRect();
    if (!canvasRect) return;
    
    const elementCenterX = element.position.x + (element.size?.width || 200) / 2;
    const elementCenterY = element.position.y + (element.size?.height || 150) / 2;
    
    // Set pan to center the element
    const newPanX = canvasRect.width / 2 - elementCenterX * zoom;
    const newPanY = canvasRect.height / 2 - elementCenterY * zoom;
    
    setPan({ x: newPanX, y: newPanY });
    
    // Select the element
    setSelectedElement(elementId);
    setSelectedElements([elementId]);
    
    toast.info('Centered on element');
  };

  const handleAICreateStickyNote = async (data) => {
    try {
      const response = await apiCreateElement({
        type: 'sticky',
        position: data.position || { x: 100, y: 100 },
        content: data.content || '',
        sticky_title: data.sticky_title || '', // Optional title for product element stickies
        element_source: data.element_source || '', // Track source (strategy, persona, etc.)
        color: data.color || currentColor,
        size: data.size || { width: 220, height: 180 }, // Slightly larger default
      });
      
      // Extract data from axios response
      const newElement = response?.data || response;
      
      if (newElement && newElement.element_id) {
        setElements(prev => [...prev, newElement]);
        emitElementCreate(newElement);
      }
      return newElement;
    } catch (err) {
      console.error('Failed to create sticky:', err);
      throw err;
    }
  };

  const handleAIUpdateElement = async (elementId, updateData) => {
    try {
      await apiUpdateElement(elementId, updateData);
      setElements(prev => prev.map(el => 
        el.element_id === elementId ? { ...el, ...updateData } : el
      ));
      
      const element = elements.find(el => el.element_id === elementId);
      if (element) {
        emitElementUpdate({ ...element, ...updateData });
      }
    } catch (err) {
      console.error('Failed to update element:', err);
      throw err;
    }
  };

  // Create a frame with optional stickies inside
  const handleAICreateFrame = async (data) => {
    try {
      const frameWidth = data.width || 500;
      const frameHeight = data.height || 400;
      const framePosition = data.position || { x: 100, y: 100 };
      
      // Create the frame first
      const frameResponse = await apiCreateElement({
        type: 'frame',
        position: framePosition,
        content: '',
        frame_title: data.title || 'AI Suggestions',
        size: { width: frameWidth, height: frameHeight },
        color: data.color || '#f3f4f6', // Default grey for frames
        z_index: 0, // Frames stay at bottom
      });
      
      const frameElement = frameResponse?.data || frameResponse;
      if (frameElement) {
        setElements(prev => [...prev, frameElement]);
        emitElementCreate(frameElement);
      }
      
      return frameElement;
    } catch (err) {
      console.error('Failed to create frame:', err);
      throw err;
    }
  };

  // Create a text element (for instructions)
  const handleAICreateText = async (data) => {
    try {
      const response = await apiCreateElement({
        type: 'text',
        position: data.position || { x: 100, y: 100 },
        content: data.content || '',
        size: data.size || { width: 400, height: 50 },
      });
      
      const newElement = response?.data || response;
      
      if (newElement && newElement.element_id) {
        setElements(prev => [...prev, newElement]);
        emitElementCreate(newElement);
      }
      return newElement;
    } catch (err) {
      console.error('Failed to create text:', err);
      throw err;
    }
  };

  const handleClearAISelection = () => {
    setSelectedElement(null);
    setSelectedElements([]);
  };

  // Get selected element objects for AI panel
  const selectedElementObjects = useMemo(() => {
    return selectedElements
      .map(id => elements.find(el => el.element_id === id))
      .filter(Boolean);
  }, [selectedElements, elements]);

  // Handle reaction toggle
  const handleReactionToggle = async (elementId, emoji) => {
    const element = elements.find(el => el.element_id === elementId);
    if (!element) return;

    // Use currentUser which works for both authenticated users and guests
    const activeUser = currentUser;
    if (!activeUser) return;

    const hasReacted = element.reactions?.some(
      r => r.emoji === emoji && r.user_id === activeUser.user_id
    );

    try {
      if (hasReacted) {
        // For guests, reactions might not have a specific endpoint - just update locally
        if (isGuest) {
          // Guest reaction removal - update locally only, WebSocket will sync
          emitReactionRemove(elementId, emoji, activeUser.user_id);
          setElements(prev => prev.map(el => {
            if (el.element_id === elementId) {
              const reactions = (el.reactions || []).filter(
                r => !(r.emoji === emoji && r.user_id === activeUser.user_id)
              );
              return { ...el, reactions };
            }
            return el;
          }));
        } else {
          await collaborationApi.removeReaction(spaceId, elementId, emoji);
          emitReactionRemove(elementId, emoji, activeUser.user_id);
          setElements(prev => prev.map(el => {
            if (el.element_id === elementId) {
              const reactions = (el.reactions || []).filter(
                r => !(r.emoji === emoji && r.user_id === activeUser.user_id)
              );
              return { ...el, reactions };
            }
            return el;
          }));
        }
      } else {
        // For guests, reactions might not have a specific endpoint - just update locally
        if (isGuest) {
          // Guest reaction add - update locally only, WebSocket will sync
          emitReactionAdd(elementId, emoji, activeUser.user_id);
          setElements(prev => prev.map(el => {
            if (el.element_id === elementId) {
              const reactions = [...(el.reactions || []), { emoji, user_id: activeUser.user_id, user_name: activeUser.name }];
              return { ...el, reactions };
            }
            return el;
          }));
        } else {
          await collaborationApi.addReaction(spaceId, elementId, emoji, activeUser.user_id);
          emitReactionAdd(elementId, emoji, activeUser.user_id);
          setElements(prev => prev.map(el => {
            if (el.element_id === elementId) {
              const reactions = [...(el.reactions || []), { emoji, user_id: activeUser.user_id, user_name: activeUser.name }];
              return { ...el, reactions };
            }
            return el;
          }));
        }
      }
    } catch (err) {
      toast.error('Failed to update reaction');
    }
  };

  // Handle drop from Product Elements Drawer
  const handleProductElementDrop = async (e) => {
    e.preventDefault();
    
    try {
      const jsonData = e.dataTransfer.getData('application/json');
      if (!jsonData) return;
      
      const data = JSON.parse(jsonData);
      if (data.type !== 'product-element') return;
      
      // Calculate drop position on canvas
      const rect = canvasRef.current.getBoundingClientRect();
      const x = (e.clientX - rect.left - pan.x) / zoom;
      const y = (e.clientY - rect.top - pan.y) / zoom;
      
      // Create formatted sticky note content with badge
      const badgeEmoji = {
        strategy: '🎯',
        market: '📊',
        personas: '👤',
        goals: '🏆',
        initiatives: '🚀',
        epics: '📦',
        features: '⚡',
      }[data.elementType] || '📄';
      
      // Create sticky with separate title (for special product element stickies)
      const stickyTitle = `${badgeEmoji} ${data.title}`;
      
      // Calculate appropriate size based on content length
      const contentLength = (data.content || '').length;
      const baseWidth = 260;
      const baseHeight = 200;
      // Add more height for longer content
      const additionalHeight = Math.min(Math.floor(contentLength / 100) * 30, 150);
      
      // Create the sticky note with title field
      const newElement = await handleAICreateStickyNote({
        position: { x, y },
        content: data.content || '',
        sticky_title: stickyTitle,
        color: data.color || '#fef3c7', // Type-specific background
        element_source: data.elementType, // Track source type
        size: { width: baseWidth, height: baseHeight + additionalHeight },
      });
      
      if (newElement) {
        toast.success(`Added ${data.title} to canvas`);
      }
    } catch (err) {
      console.error('Failed to handle drop:', err);
      toast.error('Failed to add element to canvas');
    }
  };

  // Invite collaborator
  const handleInvite = async () => {
    if (!inviteEmail) return;
    setInviting(true);
    try {
      await collaborationApi.inviteCollaborator(spaceId, inviteEmail);
      toast.success(`Invitation sent to ${inviteEmail}`);
      setInviteEmail('');
      const response = await collaborationApi.listCollaborators(spaceId);
      setCollaborators(response.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send invitation');
    } finally {
      setInviting(false);
    }
  };

  // Zoom controls
  const handleZoomIn = () => setZoom(prev => Math.min(prev * 1.2, MAX_ZOOM));
  const handleZoomOut = () => setZoom(prev => Math.max(prev / 1.2, MIN_ZOOM));
  const handleZoomReset = () => { setZoom(1); setPan({ x: 0, y: 0 }); };
  
  // Update refs for keyboard shortcuts
  zoomFunctionsRef.current = { zoomIn: handleZoomIn, zoomOut: handleZoomOut, zoomReset: handleZoomReset };

  // Get online status for collaborator
  const isUserOnline = (userId) => {
    return activeUsers.some(u => u.user_id === userId);
  };

  // Phase 2: Voting handlers
  const handleStartVoting = (anonymousMode) => {
    // Use the passed value, not state (which may be stale due to async updates)
    const isAnonymous = anonymousMode !== undefined ? anonymousMode : isAnonymousVoting;
    
    setIsVotingActive(true);
    setVotingEnded(false);
    setUserVotes({});
    setAllVotes({});
    setIsAnonymousVoting(isAnonymous);
    
    // Update ref immediately so vote_cast handler uses correct value
    isAnonymousVotingRef.current = isAnonymous;
    
    // Emit to other users with anonymous flag
    emitVotingStart(votesPerPerson, isAnonymous);
    toast.success(`Voting started! Each person has ${votesPerPerson} votes.${isAnonymous ? ' (Anonymous)' : ''}`);
  };

  const handleEndVoting = () => {
    setIsVotingActive(false);
    setVotingEnded(true);
    // Emit to other users
    emitVotingEnd();
    toast.success('Voting ended! Results are displayed on each item.');
  };

  const handleClearVotes = () => {
    setUserVotes({});
    setAllVotes({});
    setVotingEnded(false);
    // Emit to other users
    emitVotingClear();
    toast.info('Votes cleared.');
  };

  const handleVote = (elementId) => {
    const currentVotes = Object.values(userVotes).reduce((sum, v) => sum + v, 0);
    const elementVotes = userVotes[elementId] || 0;
    
    if (currentVotes >= votesPerPerson && elementVotes === 0) {
      toast.error('No votes remaining!');
      return;
    }
    
    const newVoteCount = elementVotes > 0 ? 0 : 1;
    
    // Toggle vote locally
    setUserVotes(prev => ({
      ...prev,
      [elementId]: newVoteCount
    }));
    
    // Only update local allVotes if not anonymous (real-time mode)
    // Use ref for consistent value
    if (!isAnonymousVotingRef.current) {
      setAllVotes(prev => ({
        ...prev,
        [elementId]: (prev[elementId] || 0) + (elementVotes > 0 ? -1 : 1)
      }));
    }
    
    // Emit vote to other users
    emitVoteCast(elementId, currentUser?.user_id, newVoteCount);
  };

  const votesRemaining = votesPerPerson - Object.values(userVotes).reduce((sum, v) => sum + v, 0);

  // Timer handlers for synchronization
  const handleTimerStart = (timeSeconds, startedBy) => {
    setIsTimerRunning(true);
    setSyncedTimeLeft(timeSeconds);
    setTimerStartedBy(startedBy);
    emitTimerStart(timeSeconds, startedBy);
  };

  const handleTimerPause = (timeLeft) => {
    setIsTimerRunning(false);
    setSyncedTimeLeft(timeLeft);
    emitTimerPause(timeLeft);
  };

  const handleTimerReset = () => {
    setIsTimerRunning(false);
    setSyncedTimeLeft(0);
    setTimerStartedBy(null);
    emitTimerReset();
  };

  const handleTimerSync = (timeLeft, isRunning) => {
    setSyncedTimeLeft(timeLeft);
    setIsTimerRunning(isRunning);
    emitTimerSync(timeLeft, isRunning);
  };

  // Phase 2: Follow mode handler
  const handleFollowUser = (targetUser) => {
    if (followingUser?.user_id === targetUser.user_id) {
      setFollowingUser(null);
      toast.info('Stopped following');
    } else {
      setFollowingUser(targetUser);
      toast.info(`Following ${targetUser.name}`);
    }
  };

  // Update pan when following user
  useEffect(() => {
    if (followingUser && remoteCursors[followingUser.user_id]) {
      const cursor = remoteCursors[followingUser.user_id];
      const rect = canvasRef.current?.getBoundingClientRect();
      if (rect) {
        const targetX = rect.width / 2 - cursor.x * zoom;
        const targetY = rect.height / 2 - cursor.y * zoom;
        setPan({ x: targetX, y: targetY });
      }
    }
  }, [followingUser, remoteCursors, zoom]);

  // Phase 2: Connector handlers
  const handleConnectorClick = (elementId) => {
    if (!connectorMode) return;
    
    if (!connectorStart) {
      setConnectorStart(elementId);
      toast.info('Click another element to connect');
    } else if (connectorStart !== elementId) {
      // Create connector
      const newConnector = {
        id: `conn_${Date.now()}`,
        startId: connectorStart,
        endId: elementId,
      };
      setConnectors(prev => [...prev, newConnector]);
      setConnectorStart(null);
      setConnectorMode(false);
      toast.success('Connector created');
    }
  };

  const handleDeleteConnector = (connectorId) => {
    setConnectors(prev => prev.filter(c => c.id !== connectorId));
    setSelectedConnector(null);
  };
  
  // Update refs for keyboard shortcuts
  deleteFunctionsRef.current = { deleteElement: handleElementDelete, deleteConnector: handleDeleteConnector };
  clipboardFunctionsRef.current = { 
    copy: handleCopy, 
    cut: handleCut, 
    paste: handlePaste, 
    undo: handleUndo, 
    deleteSelected: handleDeleteSelected 
  };

  // Timer end handler
  const handleTimerEnd = () => {
    toast.info("Time's up!", { duration: 5000 });
    // If in private mode, could reveal all notes here
  };

  // Check if user should authenticate
  // Only redirect to login if:
  // 1. Auth has finished loading (authLoading is false)
  // 2. No authenticated user
  // 3. Not a guest and no guest token in URL
  // 4. Space data has finished loading
  const guestToken = searchParams.get('guest');
  if (!authLoading && !user && !isGuest && !guestToken && !loading) {
    // No user, no guest session, and no guest token - redirect to login
    return (
      <div className={cn("min-h-screen flex items-center justify-center", colors.bgPrimary)}>
        <div className="text-center p-8">
          <p className={cn("text-lg mb-4", colors.textPrimary)}>Please log in to access this collaboration space</p>
          <Button onClick={() => navigate('/login', { state: { returnTo: `/collaboration/${spaceId}` } })}>
            Log In
          </Button>
        </div>
      </div>
    );
  }

  // Show loading while auth is checking or space is loading
  if (authLoading || loading) {
    return (
      <div className={cn("min-h-screen flex items-center justify-center", colors.bgPrimary)}>
        <Loader2 className={cn("w-8 h-8 animate-spin", colors.textAccent)} />
      </div>
    );
  }

  if (error) {
    return (
      <div className={cn("min-h-screen flex items-center justify-center", colors.bgPrimary)}>
        <div className="text-center">
          <p className="text-red-500 mb-4">{error}</p>
          <Button onClick={() => navigate(-1)}>Go Back</Button>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("min-h-screen flex flex-col", colors.bgPrimary)}>
      {/* Top Header Bar */}
      <div className={cn("px-4 py-2 border-b flex items-center justify-between", colors.bgSecondary, colors.borderPrimary)}>
        <div className="flex items-center gap-4">
          {/* SR Logo instead of Back button */}
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center text-white font-bold text-sm">
              SR
            </div>
          </div>
          <div>
            <h1 className={cn("text-lg font-semibold", colors.textPrimary)}>{space?.name}</h1>
            <p className={cn("text-xs", colors.textMuted)}>
              {connected ? (
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                  {activeUsers.length} online
                </span>
              ) : (
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-gray-400" />
                  Connecting...
                </span>
              )}
            </p>
          </div>
        </div>

        {/* Center: Action Tools */}
        <div className="flex items-center gap-1">
          {/* Phase 2: Facilitation Tools */}
          <FacilitationTimer 
            onTimerEnd={handleTimerEnd} 
            colors={colors}
            isTimerRunning={isTimerRunning}
            syncedTimeLeft={syncedTimeLeft}
            timerStartedBy={timerStartedBy}
            onStartTimer={handleTimerStart}
            onPauseTimer={handleTimerPause}
            onResetTimer={handleTimerReset}
            onSyncTimer={handleTimerSync}
            currentUserName={currentUser?.name || 'Unknown'}
          />
          
          <PrivateModeToggle 
            isPrivate={isPrivateMode} 
            onToggle={() => setIsPrivateMode(!isPrivateMode)} 
            colors={colors} 
          />
          
          <DotVotingPanel
            isVoting={isVotingActive}
            onStartVoting={handleStartVoting}
            onEndVoting={handleEndVoting}
            onClearVotes={handleClearVotes}
            votesPerPerson={votesPerPerson}
            onVotesPerPersonChange={setVotesPerPerson}
            votes={{ remaining: votesRemaining }}
            colors={colors}
            isAnonymous={isAnonymousVoting}
            onAnonymousToggle={setIsAnonymousVoting}
            hasVoteResults={votingEnded && Object.values(allVotes).some(v => v > 0)}
          />
        </div>

        <div className="flex items-center gap-2">
          {/* Zoom controls */}
          <div className="flex items-center gap-1 mr-2">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="sm" onClick={handleZoomOut}>
                    <ZoomOut className="w-4 h-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Zoom Out (-)</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            
            <span className={cn("text-sm min-w-[3.5rem] text-center font-medium", colors.textMuted)}>
              {Math.round(zoom * 100)}%
            </span>
            
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="sm" onClick={handleZoomIn}>
                    <ZoomIn className="w-4 h-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Zoom In (+)</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="sm" onClick={handleZoomReset}>
                    <Maximize2 className="w-4 h-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Reset View (0)</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          {/* Active users avatars - click to follow */}
          <div className="flex -space-x-2 mr-2">
            {activeUsers.slice(0, 5).map((u, i) => {
              const avatarStyle = u.avatar_id ? getAvatarStyle(u.avatar_id) : null;
              const hasProfilePic = u.avatar && !u.avatar_id;
              
              return (
                <TooltipProvider key={u.user_id || i}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        className={cn(
                          "w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium text-white border-2 transition-all overflow-hidden",
                          followingUser?.user_id === u.user_id 
                            ? "border-indigo-500 ring-2 ring-indigo-400 scale-110" 
                            : "border-white ring-2 ring-green-400 hover:scale-105"
                        )}
                        style={avatarStyle ? avatarStyle : (hasProfilePic ? {} : { backgroundColor: u.color || CURSOR_COLORS[i % CURSOR_COLORS.length].value })}
                        onClick={() => u.user_id !== user?.user_id && handleFollowUser(u)}
                      >
                        {hasProfilePic ? (
                          <img src={u.avatar} alt={u.name} className="w-full h-full object-cover" />
                        ) : avatarStyle ? null : (
                          u.name?.charAt(0).toUpperCase() || '?'
                        )}
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>
                        {u.name} {u.user_id === user?.user_id ? '(you)' : '(click to follow)'}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              );
            })}
            {activeUsers.length > 5 && (
              <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center text-xs font-medium border-2 border-white">
                +{activeUsers.length - 5}
              </div>
            )}
          </div>


          {/* Share button - only for owners/collaborators, not guests */}
          {!isGuest && (
            <>
              <Button variant="outline" size="sm" className="gap-2" onClick={() => setShowShareModal(true)}>
                <Share2 className="w-4 h-4" />
                Share
              </Button>
              
              {/* Share Modal (new component with full sharing features) */}
              <ShareModal
                open={showShareModal}
                onOpenChange={setShowShareModal}
                spaceId={spaceId}
                spaceName={space?.name || 'Collaboration Space'}
                isOwner={space?.is_owner || space?.created_by === user?.user_id}
                collaborators={collaborators}
                onCollaboratorsChange={async () => {
                  const response = await collaborationApi.listCollaborators(spaceId);
                  setCollaborators(response.data);
                }}
                colors={colors}
              />
            </>
          )}
          
          {/* Guest indicator badge */}
          {isGuest && (
            <Badge variant="secondary" className="gap-1">
              <Users className="w-3 h-3" />
              Guest ({guestSession?.role === 'edit' ? 'Can Edit' : 'View Only'})
            </Badge>
          )}
          
          {/* Keyboard shortcuts dialog */}
          <Dialog open={showShortcutsModal} onOpenChange={setShowShortcutsModal}>
            <DialogTrigger asChild>
              <Button variant="ghost" size="sm" className="gap-1">
                <Info className="w-4 h-4" />
              </Button>
            </DialogTrigger>
            <DialogContent className={cn(colors.bgCard, "max-w-md")}>
              <DialogHeader>
                <DialogTitle className={colors.textPrimary}>Keyboard Shortcuts</DialogTitle>
              </DialogHeader>
              <div className="mt-4 space-y-4">
                <div>
                  <h4 className="font-semibold text-sm mb-2 text-blue-600">Tools</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="flex items-center gap-2">
                      <kbd className="px-2 py-1 bg-gray-100 rounded font-mono text-xs">H</kbd>
                      <span>Hand (Pan)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <kbd className="px-2 py-1 bg-gray-100 rounded font-mono text-xs">V</kbd>
                      <span>Select</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <kbd className="px-2 py-1 bg-gray-100 rounded font-mono text-xs">S</kbd>
                      <span>Sticky Note</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <kbd className="px-2 py-1 bg-gray-100 rounded font-mono text-xs">F</kbd>
                      <span>Frame</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <kbd className="px-2 py-1 bg-gray-100 rounded font-mono text-xs">T</kbd>
                      <span>Text</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <kbd className="px-2 py-1 bg-gray-100 rounded font-mono text-xs">L</kbd>
                      <span>Connector</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <kbd className="px-2 py-1 bg-gray-100 rounded font-mono text-xs">P</kbd>
                      <span>Pen</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <kbd className="px-2 py-1 bg-gray-100 rounded font-mono text-xs">R</kbd>
                      <span>Rectangle</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <kbd className="px-2 py-1 bg-gray-100 rounded font-mono text-xs">O</kbd>
                      <span>Oval/Circle</span>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h4 className="font-semibold text-sm mb-2 text-green-600">Selection</h4>
                  <div className="grid grid-cols-1 gap-2 text-sm">
                    <div className="flex items-center gap-2">
                      <kbd className="px-2 py-1 bg-gray-100 rounded font-mono text-xs">Click</kbd>
                      <span>Select single element</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <kbd className="px-2 py-1 bg-gray-100 rounded font-mono text-xs">Ctrl/⌘ + Click</kbd>
                      <span>Add/remove from selection</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <kbd className="px-2 py-1 bg-gray-100 rounded font-mono text-xs">Shift + Click</kbd>
                      <span>Add/remove from selection</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <kbd className="px-2 py-1 bg-gray-100 rounded font-mono text-xs">Drag (Select tool)</kbd>
                      <span>Lasso select multiple</span>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h4 className="font-semibold text-sm mb-2 text-purple-600">View & Actions</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="flex items-center gap-2">
                      <kbd className="px-2 py-1 bg-gray-100 rounded font-mono text-xs">+</kbd>
                      <span>Zoom In</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <kbd className="px-2 py-1 bg-gray-100 rounded font-mono text-xs">-</kbd>
                      <span>Zoom Out</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <kbd className="px-2 py-1 bg-gray-100 rounded font-mono text-xs">0</kbd>
                      <span>Reset Zoom</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <kbd className="px-2 py-1 bg-gray-100 rounded font-mono text-xs">Scroll</kbd>
                      <span>Zoom</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <kbd className="px-2 py-1 bg-gray-100 rounded font-mono text-xs">Del</kbd>
                      <span>Delete</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <kbd className="px-2 py-1 bg-gray-100 rounded font-mono text-xs">Esc</kbd>
                      <span>Cancel/Deselect</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <kbd className="px-2 py-1 bg-gray-100 rounded font-mono text-xs">⌘/Ctrl + C</kbd>
                      <span>Copy</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <kbd className="px-2 py-1 bg-gray-100 rounded font-mono text-xs">⌘/Ctrl + V</kbd>
                      <span>Paste</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <kbd className="px-2 py-1 bg-gray-100 rounded font-mono text-xs">⌘/Ctrl + X</kbd>
                      <span>Cut</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <kbd className="px-2 py-1 bg-gray-100 rounded font-mono text-xs">⌘/Ctrl + Z</kbd>
                      <span>Undo</span>
                    </div>
                  </div>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Main content with left toolbar and canvas */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Toolbar - Mouse Action Tools */}
        <div className={cn("w-14 border-r flex flex-col items-center py-3 gap-1", colors.bgSecondary, colors.borderPrimary)}>
          {/* Grab Tool (Hand) */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant={activeTool === 'grab' ? 'default' : 'ghost'}
                  size="sm"
                  className="w-10 h-10"
                  onClick={() => { setActiveTool('grab'); setConnectorMode(false); }}
                >
                  <Hand className="w-5 h-5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">Hand Tool (H) - Pan & Move</TooltipContent>
            </Tooltip>
          </TooltipProvider>

          {/* Select Tool (Pointer) */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant={activeTool === 'select' ? 'default' : 'ghost'}
                  size="sm"
                  className="w-10 h-10"
                  onClick={() => { setActiveTool('select'); setConnectorMode(false); }}
                >
                  <MousePointer2 className="w-5 h-5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">Select Tool (V)</TooltipContent>
            </Tooltip>
          </TooltipProvider>

          {/* Undo Button */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-10 h-10"
                  onClick={handleUndo}
                  disabled={undoStack.length === 0}
                >
                  <Undo2 className="w-5 h-5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">Undo (⌘/Ctrl + Z)</TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <div className="h-px w-8 bg-gray-300 my-2" />

          {/* Frame Tool - MOVED ABOVE STICKY */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant={activeTool === 'frame' ? 'default' : 'ghost'}
                  size="sm"
                  className="w-10 h-10"
                  onClick={() => { setActiveTool('frame'); setConnectorMode(false); }}
                >
                  <Square className="w-5 h-5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">Frame (F)</TooltipContent>
            </Tooltip>
          </TooltipProvider>

          {/* Sticky Note Tool */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant={activeTool === 'sticky' ? 'default' : 'ghost'}
                  size="sm"
                  className="w-10 h-10"
                  onClick={() => { setActiveTool('sticky'); setConnectorMode(false); }}
                >
                  <StickyNote className="w-5 h-5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">Sticky Note (S)</TooltipContent>
            </Tooltip>
          </TooltipProvider>

          {/* Text Tool */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant={activeTool === 'text' ? 'default' : 'ghost'}
                  size="sm"
                  className="w-10 h-10"
                  onClick={() => { setActiveTool('text'); setConnectorMode(false); }}
                >
                  <Type className="w-5 h-5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">Text (T)</TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <div className="h-px w-8 bg-gray-300 my-2" />

          {/* Shape Tool */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant={activeTool === 'shape' ? 'default' : 'ghost'}
                  size="sm"
                  className="w-10 h-10"
                  onClick={() => { setActiveTool('shape'); setConnectorMode(false); }}
                >
                  <Shapes className="w-5 h-5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">Shapes (R/O/L)</TooltipContent>
            </Tooltip>
          </TooltipProvider>

          {/* Shape type selector (visible when shape tool is active) */}
          {activeTool === 'shape' && (
            <div className="flex flex-col gap-1 mt-2 p-1 bg-white rounded-lg shadow-lg border">
              <button
                className={cn(
                  "w-8 h-8 rounded flex items-center justify-center",
                  shapeType === 'rectangle' ? "bg-blue-100 text-blue-600" : "hover:bg-gray-100"
                )}
                onClick={() => setShapeType('rectangle')}
                title="Rectangle (R)"
              >
                <RectangleHorizontal className="w-4 h-4" />
              </button>
              <button
                className={cn(
                  "w-8 h-8 rounded flex items-center justify-center",
                  shapeType === 'circle' ? "bg-blue-100 text-blue-600" : "hover:bg-gray-100"
                )}
                onClick={() => setShapeType('circle')}
                title="Circle (O)"
              >
                <CircleDot className="w-4 h-4" />
              </button>
              <button
                className={cn(
                  "w-8 h-8 rounded flex items-center justify-center",
                  shapeType === 'triangle' ? "bg-blue-100 text-blue-600" : "hover:bg-gray-100"
                )}
                onClick={() => setShapeType('triangle')}
                title="Triangle"
              >
                <Triangle className="w-4 h-4" />
              </button>
              <button
                className={cn(
                  "w-8 h-8 rounded flex items-center justify-center",
                  shapeType === 'line' ? "bg-blue-100 text-blue-600" : "hover:bg-gray-100"
                )}
                onClick={() => setShapeType('line')}
                title="Line (L)"
              >
                <Minus className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Color picker for sticky notes */}
          {activeTool === 'sticky' && (
            <div className="flex flex-col gap-1 mt-2 p-1.5 bg-white rounded-lg shadow-lg border">
              {STICKY_COLORS.slice(0, 5).map(color => (
                <button
                  key={color.value}
                  className={cn(
                    "w-6 h-6 rounded-full border-2 transition-transform",
                    currentColor === color.value ? "scale-110 border-gray-600" : "border-transparent hover:scale-105"
                  )}
                  style={{ backgroundColor: color.value }}
                  onClick={() => setCurrentColor(color.value)}
                  title={color.name}
                />
              ))}
              {/* Custom color picker */}
              <label className="relative w-6 h-6 rounded-full border-2 border-dashed border-gray-400 hover:border-gray-600 cursor-pointer transition-colors overflow-hidden">
                <input
                  type="color"
                  value={currentColor}
                  onChange={(e) => setCurrentColor(e.target.value)}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  title="Custom Color"
                />
                <div 
                  className="w-full h-full rounded-full"
                  style={{ 
                    background: `conic-gradient(red, yellow, lime, aqua, blue, magenta, red)`,
                  }}
                />
              </label>
            </div>
          )}
        </div>

        {/* Canvas */}
        <div
          ref={canvasRef}
          className={cn(
            "flex-1 overflow-hidden relative",
            activeTool === 'grab' ? (isPanning ? "cursor-grabbing" : "cursor-grab") :
            activeTool === 'select' ? (isLassoing ? "cursor-crosshair" : "cursor-default") :
            (activeTool === 'sticky' || activeTool === 'frame' || activeTool === 'text' || activeTool === 'shape') ? "cursor-crosshair" :
            activeTool === 'connector' ? "cursor-crosshair" :
            activeTool === 'pen' ? "cursor-crosshair" :
            "cursor-default"
          )}
          style={{ backgroundColor: colors.bgPrimary.includes('dark') ? '#1f2937' : '#f9fafb' }}
          onMouseDown={(e) => {
            if (e.target !== canvasRef.current) return;
            
            const rect = canvasRef.current.getBoundingClientRect();
            const x = (e.clientX - rect.left - pan.x) / zoom;
            const y = (e.clientY - rect.top - pan.y) / zoom;
            
            // Pan with grab tool or middle mouse button
            if ((activeTool === 'grab' || e.button === 1)) {
              setIsPanning(true);
              setPanStart({ x: e.clientX, y: e.clientY });
            }
            // Lasso selection with select tool
            else if (activeTool === 'select') {
              setIsLassoing(true);
              setLassoStart({ x, y });
              setLassoEnd({ x, y });
            }
          }}
          onMouseUp={handleDragEnd}
          onMouseLeave={handleDragEnd}
          onMouseMove={handleCanvasMouseMove}
          onClick={handleCanvasClick}
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleProductElementDrop}
        >
        {/* Grid background */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: `radial-gradient(circle, ${colors.borderPrimary.includes('dark') ? '#374151' : '#d1d5db'} 1px, transparent 1px)`,
            backgroundSize: `${20 * zoom}px ${20 * zoom}px`,
            backgroundPosition: `${pan.x}px ${pan.y}px`,
          }}
        />

        {/* Elements container */}
        <div
          className="absolute"
          style={{
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
            transformOrigin: '0 0',
          }}
        >
          {/* Render frames first (behind stickies) */}
          {elements.filter(el => el.type === 'frame').map(element => (
            <FrameElement
              key={element.element_id}
              element={element}
              isSelected={selectedElement === element.element_id || selectedElements.includes(element.element_id)}
              isEditing={editingElement === element.element_id}
              isResizing={resizingElement === element.element_id}
              isDragging={isDraggingMultiple && selectedElements.includes(element.element_id)}
              isMultiSelected={selectedElements.length > 1 && selectedElements.includes(element.element_id)}
              onSelect={(e) => handleElementClick(e || { stopPropagation: () => {} }, element)}
              onStartEdit={() => setEditingElement(element.element_id)}
              onEndEdit={() => setEditingElement(null)}
              onDragStart={(e) => handleElementDragStart(e, element)}
              onResizeStart={(e, handle) => handleResizeStart(e, element, handle)}
              onDelete={() => handleElementDelete(element.element_id)}
              onColorChange={(color) => handleElementColorUpdate(element.element_id, color)}
              onContentChange={(content) => handleElementContentUpdate(element.element_id, content, true)}
              colors={colors}
            />
          ))}

          {/* Render sticky notes */}
          {elements.filter(el => el.type === 'sticky').map(element => (
            <StickyNoteElement
              key={element.element_id}
              element={element}
              isSelected={selectedElement === element.element_id || selectedElements.includes(element.element_id)}
              isEditing={editingElement === element.element_id}
              isResizing={resizingElement === element.element_id}
              isDragging={isDraggingMultiple && selectedElements.includes(element.element_id)}
              isMultiSelected={selectedElements.length > 1 && selectedElements.includes(element.element_id)}
              userId={currentUser?.user_id}
              userName={currentUser?.name}
              collaborators={collaborators}
              onSelect={(e) => handleElementClick(e || { stopPropagation: () => {} }, element)}
              onStartEdit={() => setEditingElement(element.element_id)}
              onEndEdit={() => setEditingElement(null)}
              onDragStart={(e) => handleElementDragStart(e, element)}
              onResizeStart={(e, handle) => handleResizeStart(e, element, handle)}
              onDelete={() => handleElementDelete(element.element_id)}
              onColorChange={(color) => handleElementColorUpdate(element.element_id, color)}
              onContentChange={(content) => handleElementContentUpdate(element.element_id, content)}
              onTitleChange={(title) => handleStickyTitleUpdate(element.element_id, title)}
              onReaction={(emoji) => handleReactionToggle(element.element_id, emoji)}
              onBringForward={() => handleBringForward(element.element_id)}
              onSendBackward={() => handleSendBackward(element.element_id)}
              isPrivate={isPrivateMode && element.created_by === currentUser?.user_id}
              isVotingActive={isVotingActive}
              votingEnded={votingEnded}
              voteCount={allVotes[element.element_id] || 0}
              hasVoted={!!userVotes[element.element_id]}
              onVote={() => handleVote(element.element_id)}
              colors={colors}
            />
          ))}
          
          {/* Render shapes */}
          {elements.filter(el => el.type === 'shape').map(element => (
            <ShapeElement
              key={element.element_id}
              element={element}
              isSelected={selectedElement === element.element_id || selectedElements.includes(element.element_id)}
              isResizing={resizingElement === element.element_id}
              isMultiSelected={selectedElements.length > 1 && selectedElements.includes(element.element_id)}
              isDragging={isDraggingMultiple && selectedElements.includes(element.element_id)}
              onSelect={(e) => handleElementClick(e || { stopPropagation: () => {} }, element)}
              onDragStart={(e) => handleElementDragStart(e, element)}
              onResizeStart={(e, handle) => handleResizeStart(e, element, handle)}
              onDelete={() => handleElementDelete(element.element_id)}
              onColorChange={(color) => handleElementColorUpdate(element.element_id, color)}
              onBringForward={() => handleBringForward(element.element_id)}
              onSendBackward={() => handleSendBackward(element.element_id)}
              colors={colors}
            />
          ))}
          
          {/* Render text elements */}
          {elements.filter(el => el.type === 'text').map(element => (
            <TextElement
              key={element.element_id}
              element={element}
              isSelected={selectedElement === element.element_id || selectedElements.includes(element.element_id)}
              isEditing={editingElement === element.element_id}
              isResizing={resizingElement === element.element_id}
              isDragging={isDraggingMultiple && selectedElements.includes(element.element_id)}
              isMultiSelected={selectedElements.length > 1 && selectedElements.includes(element.element_id)}
              onSelect={(e) => handleElementClick(e || { stopPropagation: () => {} }, element)}
              onStartEdit={() => setEditingElement(element.element_id)}
              onEndEdit={() => setEditingElement(null)}
              onDragStart={(e) => handleElementDragStart(e, element)}
              onResizeStart={(e, handle) => handleResizeStart(e, element, handle)}
              onDelete={() => handleElementDelete(element.element_id)}
              onContentChange={(content) => handleElementContentUpdate(element.element_id, content)}
              onBringForward={() => handleBringForward(element.element_id)}
              onSendBackward={() => handleSendBackward(element.element_id)}
              colors={colors}
            />
          ))}
        </div>
        
        {/* Lasso selection rectangle */}
        {isLassoing && (
          <div
            className="absolute border-2 border-blue-500 bg-blue-500/10 pointer-events-none z-40"
            style={{
              left: pan.x + Math.min(lassoStart.x, lassoEnd.x) * zoom,
              top: pan.y + Math.min(lassoStart.y, lassoEnd.y) * zoom,
              width: Math.abs(lassoEnd.x - lassoStart.x) * zoom,
              height: Math.abs(lassoEnd.y - lassoStart.y) * zoom,
            }}
          />
        )}

        {/* Remote cursors */}
        {Object.entries(remoteCursors).map(([oderId, cursor]) => {
          const cursorAvatarStyle = cursor.avatar_id ? getAvatarStyle(cursor.avatar_id) : null;
          const hasCursorProfilePic = cursor.avatar && !cursor.avatar_id;
          
          return (
            <div
              key={oderId}
              className="absolute pointer-events-none z-50 transition-all duration-100 ease-out"
              style={{
                left: pan.x + cursor.x * zoom,
                top: pan.y + cursor.y * zoom,
              }}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill={cursor.color}
                className="drop-shadow-md"
              >
                <path d="M5.5 3.21V20.8c0 .45.54.67.85.35l4.86-4.86a.5.5 0 0 1 .35-.15h6.87a.5.5 0 0 0 .35-.85L6.35 2.86a.5.5 0 0 0-.85.35z" />
              </svg>
              {/* Name badge with avatar */}
              <div
                className="absolute left-5 top-4 flex items-center gap-1.5 text-xs text-white px-1.5 py-0.5 rounded-full whitespace-nowrap shadow-md"
                style={{ backgroundColor: cursor.color }}
              >
                {/* Avatar thumbnail */}
                {hasCursorProfilePic ? (
                  <img 
                    src={cursor.avatar} 
                    alt={cursor.name} 
                    className="w-5 h-5 rounded-full object-cover border border-white/30"
                  />
                ) : cursorAvatarStyle ? (
                  <div 
                    className="w-5 h-5 rounded-full border border-white/30"
                    style={cursorAvatarStyle}
                  />
                ) : (
                  <div 
                    className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-medium bg-white/20 border border-white/30"
                  >
                    {cursor.name?.charAt(0).toUpperCase() || '?'}
                  </div>
                )}
                <span>{cursor.name}</span>
              </div>
            </div>
          );
        })}

        {/* Connector lines SVG overlay */}
        <svg
          className="absolute inset-0 pointer-events-none"
          style={{
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
            transformOrigin: '0 0',
          }}
        >
          <ConnectorDefs />
          {connectors.map(conn => {
            const startEl = elements.find(e => e.element_id === conn.startId);
            const endEl = elements.find(e => e.element_id === conn.endId);
            if (!startEl || !endEl) return null;
            
            const startX = startEl.position.x + (startEl.size?.width || 200) / 2;
            const startY = startEl.position.y + (startEl.size?.height || 200) / 2;
            const endX = endEl.position.x + (endEl.size?.width || 200) / 2;
            const endY = endEl.position.y + (endEl.size?.height || 200) / 2;
            
            return (
              <g key={conn.id} style={{ pointerEvents: 'auto', cursor: 'pointer' }}>
                <line
                  x1={startX}
                  y1={startY}
                  x2={endX}
                  y2={endY}
                  stroke={selectedConnector === conn.id ? "#3b82f6" : "#9ca3af"}
                  strokeWidth={selectedConnector === conn.id ? 3 : 2}
                  strokeDasharray={selectedConnector === conn.id ? "none" : "5,5"}
                  onClick={(e) => { e.stopPropagation(); setSelectedConnector(conn.id); }}
                />
                {selectedConnector === conn.id && (
                  <g 
                    transform={`translate(${(startX + endX) / 2 - 12}, ${(startY + endY) / 2 - 12})`}
                    onClick={(e) => { e.stopPropagation(); handleDeleteConnector(conn.id); }}
                    style={{ cursor: 'pointer' }}
                  >
                    <circle r="12" fill="white" stroke="#ef4444" strokeWidth="2" cx="12" cy="12" />
                    <text x="12" y="16" textAnchor="middle" fill="#ef4444" fontSize="16" fontWeight="bold">×</text>
                  </g>
                )}
              </g>
            );
          })}
        </svg>

        {/* Tool hint */}
        {(activeTool === 'sticky' || activeTool === 'frame' || activeTool === 'text' || activeTool === 'shape') && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 bg-black/70 text-white rounded-lg text-sm">
            Click anywhere to place a {
              activeTool === 'sticky' ? 'sticky note' : 
              activeTool === 'frame' ? 'frame' :
              activeTool === 'text' ? 'text note' :
              `${shapeType} shape`
            } • Press ESC to cancel
          </div>
        )}
        
        {/* Connector mode hint */}
        {connectorMode && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm">
            {connectorStart ? 'Click another element to connect' : 'Click an element to start connector'} • Press ESC to cancel
          </div>
        )}
      </div>
      
      {/* Follow mode indicator */}
      <FollowModeIndicator 
        followingUser={followingUser} 
        onStopFollowing={() => setFollowingUser(null)} 
        colors={colors}
      />
      
      {/* Product Elements Drawer - only for non-guests */}
      <ProductElementsDrawer
        productId={space?.product_id}
        onCreateStickyNote={handleAICreateStickyNote}
        isGuest={isGuest}
        colors={colors}
        isOpen={openDrawer === 'elements'}
        onOpenChange={(isOpen) => setOpenDrawer(isOpen ? 'elements' : null)}
        otherDrawerOpen={openDrawer === 'ai'}
      />
      
      {/* AI Co-Pilot Panel - only for non-guests */}
      <CollaborationAIPanel
        spaceId={spaceId}
        spaceName={space?.name || 'Collaboration Space'}
        productId={space?.product_id}
        elements={elements}
        selectedElements={selectedElementObjects}
        onCreateStickyNote={handleAICreateStickyNote}
        onCreateFrame={handleAICreateFrame}
        onCreateText={handleAICreateText}
        onUpdateElement={handleAIUpdateElement}
        onCenterElement={handleCenterElement}
        onClearSelection={handleClearAISelection}
        isGuest={isGuest}
        colors={colors}
        viewportInfo={{
          pan,
          zoom,
          width: canvasRef.current?.clientWidth || 1200,
          height: canvasRef.current?.clientHeight || 800,
        }}
        isOpen={openDrawer === 'ai'}
        onOpenChange={(isOpen) => setOpenDrawer(isOpen ? 'ai' : null)}
        otherDrawerOpen={openDrawer === 'elements'}
      />
      </div>
    </div>
  );
};

// Collaborator List Component
const CollaboratorList = ({ collaborators, activeUsers, colors, showPending = false }) => {
  if (collaborators.length === 0) {
    return (
      <p className={cn("text-center py-4 text-sm", colors.textMuted)}>
        {showPending ? 'No pending invitations' : 'No collaborators yet'}
      </p>
    );
  }

  const isOnline = (userId) => activeUsers.some(u => u.user_id === userId);

  return (
    <div className="space-y-2 max-h-60 overflow-y-auto">
      {collaborators.map((c, i) => (
        <div 
          key={i} 
          className={cn(
            "flex items-center justify-between p-2 rounded-lg",
            colors.bgTertiary
          )}
        >
          <div className="flex items-center gap-3">
            <div className="relative">
              <div 
                className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm"
                style={{ backgroundColor: CURSOR_COLORS[i % CURSOR_COLORS.length].value }}
              >
                {c.name?.charAt(0).toUpperCase() || c.email?.charAt(0).toUpperCase()}
              </div>
              {isOnline(c.user_id) && (
                <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-white" />
              )}
            </div>
            <div>
              <p className={cn("text-sm font-medium", colors.textPrimary)}>
                {c.name || c.email?.split('@')[0]}
                {!c.joined_at && <span className="text-xs ml-1 text-amber-500">(pending)</span>}
              </p>
              <p className={cn("text-xs", colors.textMuted)}>{c.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isOnline(c.user_id) ? (
              <Badge variant="outline" className="text-green-600 border-green-200 bg-green-50">
                <Circle className="w-2 h-2 mr-1 fill-green-500" /> Online
              </Badge>
            ) : c.joined_at ? (
              <Badge variant="outline" className={colors.textMuted}>
                Offline
              </Badge>
            ) : (
              <Badge variant="outline" className="text-amber-600 border-amber-200 bg-amber-50">
                <Clock className="w-3 h-3 mr-1" /> Invited
              </Badge>
            )}
            <Badge variant={c.role === 'owner' ? 'default' : 'secondary'}>
              {c.role}
            </Badge>
          </div>
        </div>
      ))}
    </div>
  );
};

// Sticky Note Element Component with reaction tooltips
const StickyNoteElement = ({
  element,
  isSelected,
  isEditing,
  isResizing = false,
  isDragging = false,
  isMultiSelected = false,
  userId,
  userName,
  collaborators,
  onSelect,
  onStartEdit,
  onEndEdit,
  onDragStart,
  onResizeStart,
  onDelete,
  onColorChange,
  onContentChange,
  onTitleChange, // New prop for title changes
  onReaction,
  onBringForward,
  onSendBackward,
  isPrivate,
  isVotingActive,
  votingEnded,
  voteCount,
  hasVoted,
  onVote,
  colors,
}) => {
  const textareaRef = useRef(null);
  const titleRef = useRef(null);
  const [localContent, setLocalContent] = useState(element.content || '');
  const [localTitle, setLocalTitle] = useState(element.sticky_title || '');
  
  // Check if this is a special sticky (from product elements drawer)
  const hasTitle = !!element.sticky_title;

  // Resize handles for sticky notes
  const resizeHandles = [
    { position: 'e', cursor: 'ew-resize', className: 'right-0 top-1/2 -translate-y-1/2 w-2 h-8' },
    { position: 's', cursor: 'ns-resize', className: 'bottom-0 left-1/2 -translate-x-1/2 h-2 w-8' },
    { position: 'se', cursor: 'nwse-resize', className: 'right-0 bottom-0 w-3 h-3' },
  ];

  useEffect(() => {
    if (isEditing) {
      // Focus title if it exists, otherwise content
      if (hasTitle && titleRef.current) {
        titleRef.current.focus();
      } else if (textareaRef.current) {
        textareaRef.current.focus();
        setTimeout(() => textareaRef.current?.select(), 10);
      }
    }
  }, [isEditing, hasTitle]);

  // Controlled component pattern - sync with element when not editing
  const displayContent = isEditing ? localContent : (element.content || '');
  const displayTitle = isEditing ? localTitle : (element.sticky_title || '');

  const handleStartEdit = () => {
    setLocalContent(element.content || '');
    setLocalTitle(element.sticky_title || '');
    onStartEdit();
  };

  const handleSave = () => {
    onContentChange(localContent);
    if (hasTitle && onTitleChange) {
      onTitleChange(localTitle);
    }
    onEndEdit();
  };

  // Group reactions and get user names
  const groupedReactions = (element.reactions || []).reduce((acc, r) => {
    if (!acc[r.emoji]) {
      acc[r.emoji] = { count: 0, hasUserReacted: false, users: [] };
    }
    acc[r.emoji].count++;
    if (r.user_id === userId) acc[r.emoji].hasUserReacted = true;
    
    // Get user name from collaborators or reaction data
    const collaborator = collaborators?.find(c => c.user_id === r.user_id);
    const name = r.user_name || collaborator?.name || collaborator?.email?.split('@')[0] || 'Someone';
    acc[r.emoji].users.push(name);
    
    return acc;
  }, {});

  return (
    <div
      className={cn(
        "absolute group cursor-move transition-opacity",
        isSelected && "ring-2 ring-blue-500",
        isDragging && "opacity-70 shadow-2xl"
      )}
      style={{
        left: element.position.x,
        top: element.position.y,
        width: element.size?.width || 200,
        height: element.size?.height || 200,
        minHeight: element.size?.height || 200,
        zIndex: element.z_index || 1, // Default z-index of 1 for non-frames
      }}
      onClick={(e) => { e.stopPropagation(); onSelect(e); }}
      onMouseDown={onDragStart}
      onDoubleClick={handleStartEdit}
    >
      {/* Private mode overlay */}
      {isPrivate && (
        <div className="absolute inset-0 bg-gray-400 rounded-lg flex items-center justify-center z-10">
          <EyeOff className="w-8 h-8 text-gray-600" />
          <span className="ml-2 text-gray-600 font-medium">Hidden</span>
        </div>
      )}
      
      {/* Vote indicator - show during voting or when voting has ended with results */}
      {(isVotingActive || (votingEnded && voteCount > 0)) && (
        <VoteIndicator
          voteCount={voteCount}
          hasVoted={hasVoted}
          onVote={onVote}
          canVote={isVotingActive}
          colors={colors}
        />
      )}
      
      <div
        className="w-full h-full rounded-lg shadow-lg p-4 flex flex-col"
        style={{ backgroundColor: element.color || '#fef08a' }}
      >
        {isEditing ? (
          <div className="h-full flex flex-col">
            {/* Title input for special stickies */}
            {hasTitle && (
              <input
                ref={titleRef}
                type="text"
                value={localTitle}
                onChange={(e) => setLocalTitle(e.target.value)}
                className="bg-transparent border-none focus:ring-0 focus:outline-none p-0 text-gray-800 font-bold text-sm mb-2 w-full flex-shrink-0"
                placeholder="Title..."
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    textareaRef.current?.focus();
                  }
                  if (e.key === 'Escape') onEndEdit();
                }}
              />
            )}
            <div className="flex-1 min-h-0 overflow-hidden">
              <textarea
                ref={textareaRef}
                value={localContent}
                onChange={(e) => setLocalContent(e.target.value)}
                className="w-full h-full bg-transparent border-none resize-none focus:ring-0 focus:outline-none p-0 text-gray-800 text-sm overflow-auto"
                placeholder="Write your idea..."
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && e.metaKey) handleSave();
                  if (e.key === 'Escape') onEndEdit();
                }}
              />
            </div>
            <div className="flex gap-2 mt-2 flex-shrink-0">
              <Button size="sm" variant="ghost" onClick={handleSave}>
                <Check className="w-3 h-3" />
              </Button>
              <Button size="sm" variant="ghost" onClick={onEndEdit}>
                <X className="w-3 h-3" />
              </Button>
            </div>
          </div>
        ) : (
          <>
            {/* Display title in bold if present */}
            {hasTitle && displayTitle && (
              <h4 className="font-bold text-gray-800 text-sm mb-1 leading-tight">
                {displayTitle}
              </h4>
            )}
            <p className="flex-1 text-gray-800 text-sm whitespace-pre-wrap overflow-hidden">
              {element.content || (hasTitle ? '' : 'Double-click to edit')}
            </p>
            
            {/* Reactions with hover tooltips */}
            {Object.keys(groupedReactions).length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2 pt-2 border-t border-black/10">
                {Object.entries(groupedReactions).map(([emoji, data]) => (
                  <TooltipProvider key={emoji}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          onClick={(e) => { e.stopPropagation(); onReaction(emoji); }}
                          className={cn(
                            "text-xs px-1.5 py-0.5 rounded-full flex items-center gap-1 transition-colors",
                            data.hasUserReacted ? "bg-blue-200 hover:bg-blue-300" : "bg-black/10 hover:bg-black/20"
                          )}
                        >
                          {emoji} {data.count}
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="top">
                        <p className="text-xs">{data.users.join(', ')}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Action buttons */}
      {/* Toolbar - only show when single element selected (not multi-select) */}
      {isSelected && !isEditing && !isMultiSelected && (
        <div className="absolute -top-10 left-0 flex items-center gap-1 bg-white rounded-lg shadow-lg p-1 z-10">
          {STICKY_COLORS.slice(0, 5).map(color => (
            <button
              key={color.value}
              className={cn(
                "w-5 h-5 rounded-full border transition-transform hover:scale-110",
                element.color === color.value ? "border-gray-600 scale-110" : "border-transparent"
              )}
              style={{ backgroundColor: color.value }}
              onClick={(e) => { e.stopPropagation(); onColorChange(color.value); }}
            />
          ))}
          {/* Custom color picker */}
          <label className="relative w-5 h-5 rounded-full border border-dashed border-gray-400 hover:border-gray-600 cursor-pointer overflow-hidden">
            <input
              type="color"
              value={element.color || '#fef08a'}
              onChange={(e) => { e.stopPropagation(); onColorChange(e.target.value); }}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              title="Custom Color"
            />
            <div 
              className="w-full h-full rounded-full"
              style={{ background: `conic-gradient(red, yellow, lime, aqua, blue, magenta, red)` }}
            />
          </label>
          <div className="w-px h-5 bg-gray-200 mx-1" />
          {QUICK_EMOJIS.map(emoji => (
            <button
              key={emoji}
              className="text-sm hover:bg-gray-100 rounded p-1"
              onClick={(e) => { e.stopPropagation(); onReaction(emoji); }}
            >
              {emoji}
            </button>
          ))}
          <div className="w-px h-5 bg-gray-200 mx-1" />
          {/* Layer controls */}
          <button
            className="p-1 hover:bg-gray-100 rounded text-gray-500"
            onClick={(e) => { e.stopPropagation(); onBringForward(); }}
            title="Bring Forward"
          >
            <ArrowUpToLine className="w-4 h-4" />
          </button>
          <button
            className="p-1 hover:bg-gray-100 rounded text-gray-500"
            onClick={(e) => { e.stopPropagation(); onSendBackward(); }}
            title="Send Backward"
          >
            <ArrowDownToLine className="w-4 h-4" />
          </button>
          <div className="w-px h-5 bg-gray-200 mx-1" />
          <button
            className="p-1 hover:bg-red-100 rounded text-red-500"
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Resize handles - only show when single element selected */}
      {isSelected && !isEditing && !isMultiSelected && onResizeStart && resizeHandles.map(handle => (
        <div
          key={handle.position}
          className={cn(
            "absolute bg-blue-500 rounded-sm transition-opacity",
            handle.className,
            isSelected ? "opacity-100" : "opacity-0 group-hover:opacity-100"
          )}
          style={{ cursor: handle.cursor }}
          onMouseDown={(e) => { e.stopPropagation(); onResizeStart(e, handle.position); }}
        />
      ))}
    </div>
  );
};

// Frame Element Component with resize handles
const FrameElement = ({
  element,
  isSelected,
  isEditing,
  isResizing,
  isDragging = false,
  isMultiSelected = false,
  onSelect,
  onStartEdit,
  onEndEdit,
  onDragStart,
  onResizeStart,
  onDelete,
  onColorChange,
  onContentChange,
  colors,
}) => {
  const [localTitle, setLocalTitle] = useState(element.frame_title || 'Frame');

  const handleStartEdit = () => {
    setLocalTitle(element.frame_title || 'Frame');
    onStartEdit();
  };

  const handleSave = () => {
    onContentChange(localTitle);
    onEndEdit();
  };

  // Resize handle positions
  const resizeHandles = [
    { position: 'e', cursor: 'ew-resize', className: 'right-0 top-1/2 -translate-y-1/2 w-2 h-8' },
    { position: 'w', cursor: 'ew-resize', className: 'left-0 top-1/2 -translate-y-1/2 w-2 h-8' },
    { position: 's', cursor: 'ns-resize', className: 'bottom-0 left-1/2 -translate-x-1/2 h-2 w-8' },
    { position: 'n', cursor: 'ns-resize', className: 'top-0 left-1/2 -translate-x-1/2 h-2 w-8' },
    { position: 'se', cursor: 'nwse-resize', className: 'right-0 bottom-0 w-3 h-3' },
    { position: 'sw', cursor: 'nesw-resize', className: 'left-0 bottom-0 w-3 h-3' },
    { position: 'ne', cursor: 'nesw-resize', className: 'right-0 top-0 w-3 h-3' },
    { position: 'nw', cursor: 'nwse-resize', className: 'left-0 top-0 w-3 h-3' },
  ];

  return (
    <div
      className={cn(
        "absolute transition-opacity",
        isSelected && "ring-2 ring-blue-500",
        !isResizing && "cursor-move",
        isDragging && "opacity-70 shadow-2xl"
      )}
      style={{
        left: element.position.x,
        top: element.position.y,
        width: element.size?.width || 400,
        height: element.size?.height || 300,
      }}
      onClick={(e) => { e.stopPropagation(); onSelect(e); }}
      onMouseDown={onDragStart}
    >
      <div
        className="w-full h-full rounded-xl border-2 border-dashed relative"
        style={{ 
          backgroundColor: element.color || '#f3f4f6',
          borderColor: 'rgba(0,0,0,0.2)'
        }}
      >
        {/* Frame title */}
        <div
          className="absolute -top-3 left-3 px-3 py-1 rounded-md text-sm font-medium bg-white shadow-sm cursor-text"
          onDoubleClick={(e) => { e.stopPropagation(); handleStartEdit(); }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {isEditing ? (
            <div className="flex items-center gap-2">
              <Input
                value={localTitle}
                onChange={(e) => setLocalTitle(e.target.value)}
                className="h-6 text-sm w-32"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSave();
                  if (e.key === 'Escape') onEndEdit();
                }}
              />
              <Button size="sm" variant="ghost" onClick={handleSave} className="h-6 w-6 p-0">
                <Check className="w-3 h-3" />
              </Button>
            </div>
          ) : (
            element.frame_title || 'Frame'
          )}
        </div>

        {/* Resize handles - only show when single element selected */}
        {isSelected && !isEditing && !isMultiSelected && resizeHandles.map(handle => (
          <div
            key={handle.position}
            className={cn(
              "absolute bg-blue-500 rounded-sm opacity-0 group-hover:opacity-100 hover:opacity-100 transition-opacity",
              handle.className,
              isSelected && "opacity-100"
            )}
            style={{ cursor: handle.cursor }}
            onMouseDown={(e) => onResizeStart(e, handle.position)}
          />
        ))}
      </div>

      {/* Action buttons - only show when single element selected */}
      {isSelected && !isEditing && !isMultiSelected && (
        <div className="absolute -top-10 left-12 flex items-center gap-1 bg-white rounded-lg shadow-lg p-1 z-10">
          {FRAME_COLORS.map(color => (
            <button
              key={color.value}
              className={cn(
                "w-5 h-5 rounded-full border transition-transform hover:scale-110",
                element.color === color.value ? "border-gray-600 scale-110" : "border-transparent"
              )}
              style={{ backgroundColor: color.value }}
              onClick={(e) => { e.stopPropagation(); onColorChange(color.value); }}
            />
          ))}
          <div className="w-px h-5 bg-gray-200 mx-1" />
          <button
            className="p-1 hover:bg-red-100 rounded text-red-500"
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
};

// Shape colors
const SHAPE_COLORS = [
  { name: 'Blue', value: '#dbeafe' },
  { name: 'Green', value: '#dcfce7' },
  { name: 'Yellow', value: '#fef9c3' },
  { name: 'Red', value: '#fee2e2' },
  { name: 'Purple', value: '#f3e8ff' },
  { name: 'Orange', value: '#ffedd5' },
  { name: 'Gray', value: '#f3f4f6' },
  { name: 'White', value: '#ffffff' },
];

// Shape Element Component - renders actual shapes (circle, triangle, rectangle)
const ShapeElement = ({
  element,
  isSelected,
  isResizing,
  isDragging = false,
  isMultiSelected = false,
  onSelect,
  onDragStart,
  onResizeStart,
  onDelete,
  onColorChange,
  onBringForward,
  onSendBackward,
  colors,
}) => {
  const shapeType = element.shape_type || 'rectangle';
  const width = element.size?.width || 150;
  const height = element.size?.height || 150;

  // Resize handle positions
  const resizeHandles = [
    { position: 'e', cursor: 'ew-resize', className: 'right-0 top-1/2 -translate-y-1/2 w-2 h-8' },
    { position: 'w', cursor: 'ew-resize', className: 'left-0 top-1/2 -translate-y-1/2 w-2 h-8' },
    { position: 's', cursor: 'ns-resize', className: 'bottom-0 left-1/2 -translate-x-1/2 h-2 w-8' },
    { position: 'n', cursor: 'ns-resize', className: 'top-0 left-1/2 -translate-x-1/2 h-2 w-8' },
    { position: 'se', cursor: 'nwse-resize', className: 'right-0 bottom-0 w-3 h-3' },
    { position: 'sw', cursor: 'nesw-resize', className: 'left-0 bottom-0 w-3 h-3' },
    { position: 'ne', cursor: 'nesw-resize', className: 'right-0 top-0 w-3 h-3' },
    { position: 'nw', cursor: 'nwse-resize', className: 'left-0 top-0 w-3 h-3' },
  ];

  const renderShape = () => {
    const fillColor = element.color || '#dbeafe';
    const strokeColor = 'rgba(0,0,0,0.2)';
    
    switch (shapeType) {
      case 'circle':
        return (
          <svg width="100%" height="100%" viewBox={`0 0 ${width} ${height}`} className="overflow-visible">
            <ellipse
              cx={width / 2}
              cy={height / 2}
              rx={(width - 4) / 2}
              ry={(height - 4) / 2}
              fill={fillColor}
              stroke={strokeColor}
              strokeWidth="2"
            />
          </svg>
        );
      case 'triangle':
        return (
          <svg width="100%" height="100%" viewBox={`0 0 ${width} ${height}`} className="overflow-visible">
            <polygon
              points={`${width/2},4 ${width-4},${height-4} 4,${height-4}`}
              fill={fillColor}
              stroke={strokeColor}
              strokeWidth="2"
              strokeLinejoin="round"
            />
          </svg>
        );
      case 'line':
        return (
          <svg width="100%" height="100%" viewBox={`0 0 ${width} ${height}`} className="overflow-visible">
            <line
              x1="0"
              y1={height / 2}
              x2={width}
              y2={height / 2}
              stroke={element.color || '#374151'}
              strokeWidth="3"
              strokeLinecap="round"
            />
          </svg>
        );
      case 'rectangle':
      default:
        return (
          <div
            className="w-full h-full rounded-lg border-2"
            style={{ 
              backgroundColor: fillColor,
              borderColor: strokeColor
            }}
          />
        );
    }
  };

  return (
    <div
      className={cn(
        "absolute transition-opacity",
        isSelected && "ring-2 ring-blue-500 ring-offset-2",
        !isResizing && "cursor-move",
        isDragging && "opacity-70 shadow-2xl"
      )}
      style={{
        left: element.position.x,
        top: element.position.y,
        width,
        height,
        zIndex: element.z_index || 1,
      }}
      onClick={(e) => onSelect(e)}
      onMouseDown={onDragStart}
    >
      {renderShape()}

      {/* Resize handles - only show when single element selected */}
      {isSelected && !isMultiSelected && resizeHandles.map(handle => (
        <div
          key={handle.position}
          className={cn(
            "absolute bg-blue-500 rounded-sm transition-opacity",
            handle.className,
            isSelected ? "opacity-100" : "opacity-0"
          )}
          style={{ cursor: handle.cursor }}
          onMouseDown={(e) => onResizeStart(e, handle.position)}
        />
      ))}

      {/* Action buttons - only show when single element selected */}
      {isSelected && !isMultiSelected && (
        <div className="absolute -top-10 left-0 flex items-center gap-1 bg-white rounded-lg shadow-lg p-1 z-10">
          {SHAPE_COLORS.slice(0, 5).map(color => (
            <button
              key={color.value}
              className={cn(
                "w-5 h-5 rounded-full border transition-transform hover:scale-110",
                element.color === color.value ? "border-gray-600 scale-110" : "border-transparent"
              )}
              style={{ backgroundColor: color.value }}
              onClick={(e) => { e.stopPropagation(); onColorChange(color.value); }}
            />
          ))}
          {/* Custom color picker */}
          <label className="relative w-5 h-5 rounded-full border border-dashed border-gray-400 hover:border-gray-600 cursor-pointer overflow-hidden">
            <input
              type="color"
              value={element.color || '#dbeafe'}
              onChange={(e) => { e.stopPropagation(); onColorChange(e.target.value); }}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              title="Custom Color"
            />
            <div 
              className="w-full h-full rounded-full"
              style={{ background: `conic-gradient(red, yellow, lime, aqua, blue, magenta, red)` }}
            />
          </label>
          <div className="w-px h-5 bg-gray-200 mx-1" />
          {/* Layer controls */}
          <button
            className="p-1 hover:bg-gray-100 rounded text-gray-500"
            onClick={(e) => { e.stopPropagation(); onBringForward(); }}
            title="Bring Forward"
          >
            <ArrowUpToLine className="w-4 h-4" />
          </button>
          <button
            className="p-1 hover:bg-gray-100 rounded text-gray-500"
            onClick={(e) => { e.stopPropagation(); onSendBackward(); }}
            title="Send Backward"
          >
            <ArrowDownToLine className="w-4 h-4" />
          </button>
          <div className="w-px h-5 bg-gray-200 mx-1" />
          <button
            className="p-1 hover:bg-red-100 rounded text-red-500"
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
};

// Text Element Component - transparent background text
const TextElement = ({
  element,
  isSelected,
  isEditing,
  isResizing = false,
  isDragging = false,
  isMultiSelected = false,
  onSelect,
  onStartEdit,
  onEndEdit,
  onDragStart,
  onResizeStart,
  onDelete,
  onContentChange,
  onBringForward,
  onSendBackward,
  colors,
}) => {
  const textareaRef = useRef(null);
  const [localContent, setLocalContent] = useState(element.content || '');

  // Resize handles for text elements
  const resizeHandles = [
    { position: 'e', cursor: 'ew-resize', className: 'right-0 top-1/2 -translate-y-1/2 w-2 h-8' },
    { position: 's', cursor: 'ns-resize', className: 'bottom-0 left-1/2 -translate-x-1/2 h-2 w-8' },
    { position: 'se', cursor: 'nwse-resize', className: 'right-0 bottom-0 w-3 h-3' },
  ];

  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
      setTimeout(() => textareaRef.current?.select(), 10);
    }
  }, [isEditing]);

  const displayContent = isEditing ? localContent : (element.content || '');

  const handleBlur = () => {
    if (localContent !== element.content) {
      onContentChange(localContent);
    }
    onEndEdit();
  };

  return (
    <div
      className={cn(
        "absolute group transition-opacity",
        isSelected && "ring-2 ring-blue-500 ring-offset-2 rounded",
        !isEditing && !isResizing && "cursor-move",
        isDragging && "opacity-70 shadow-2xl"
      )}
      style={{
        left: element.position.x,
        top: element.position.y,
        width: element.size?.width || 200,
        minHeight: element.size?.height || 40,
        zIndex: element.z_index || 1,
      }}
      onClick={(e) => onSelect(e)}
      onDoubleClick={(e) => { e.stopPropagation(); onStartEdit(); }}
      onMouseDown={isEditing ? undefined : onDragStart}
    >
      {isEditing ? (
        <textarea
          ref={textareaRef}
          value={localContent}
          onChange={(e) => setLocalContent(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setLocalContent(element.content || '');
              onEndEdit();
            }
          }}
          className={cn(
            "w-full h-full p-2 bg-transparent border-none outline-none resize-none",
            "text-lg font-medium",
            colors.textPrimary
          )}
          placeholder="Type text..."
          style={{ 
            width: element.size?.width || 200,
            height: element.size?.height || 40,
            minHeight: element.size?.height || 40
          }}
        />
      ) : (
        <div
          className={cn(
            "p-2 text-lg font-medium whitespace-pre-wrap",
            colors.textPrimary,
            !displayContent && "text-gray-400"
          )}
          style={{ width: element.size?.width || 200 }}
        >
          {displayContent || 'Double-click to edit'}
        </div>
      )}

      {/* Action buttons - only show when single element selected */}
      {isSelected && !isEditing && !isMultiSelected && (
        <div className="absolute -top-10 left-0 flex items-center gap-1 bg-white rounded-lg shadow-lg p-1 z-10">
          {/* Layer controls */}
          <button
            className="p-1 hover:bg-gray-100 rounded text-gray-500"
            onClick={(e) => { e.stopPropagation(); onBringForward(); }}
            title="Bring Forward"
          >
            <ArrowUpToLine className="w-4 h-4" />
          </button>
          <button
            className="p-1 hover:bg-gray-100 rounded text-gray-500"
            onClick={(e) => { e.stopPropagation(); onSendBackward(); }}
            title="Send Backward"
          >
            <ArrowDownToLine className="w-4 h-4" />
          </button>
          <div className="w-px h-5 bg-gray-200 mx-1" />
          <button
            className="p-1 hover:bg-red-100 rounded text-red-500"
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Resize handles - only show when single element selected */}
      {isSelected && !isEditing && !isMultiSelected && onResizeStart && resizeHandles.map(handle => (
        <div
          key={handle.position}
          className={cn(
            "absolute bg-blue-500 rounded-sm transition-opacity",
            handle.className,
            isSelected ? "opacity-100" : "opacity-0 group-hover:opacity-100"
          )}
          style={{ cursor: handle.cursor }}
          onMouseDown={(e) => { e.stopPropagation(); onResizeStart(e, handle.position); }}
        />
      ))}
    </div>
  );
};

export default CollaborationCanvas;
